import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  getStats,
  listLeads,
  runStrategist,
  icpOptimizePreview,
  icpOptimizeApply,
  icpOptimizeLastSnapshot,
  icpOptimizeUndo,
  getPipelineBrief,
  getLearningInsights,
  diagnoseSales,
  prescribeActions,
  forecastRevenue,
  strategyAdvisor,
  automationStatus,
  getActionsToday,
  getAiImpact,
  getLeadSourceRoi,
  getBookingFunnel,
  getAiInsights,
  getMessageRanking,
  scoreAllLeads,
  generateSubjectVariants,
  requestUpgrade,
  claimActivationReward,
  getSystemHealth,
  saveSettings,
  getSettings,
  listJobs,
} from "@/lib/apiClient";
import { useAuth } from "@/contexts/AuthContext";
import StatCard from "@/components/StatCard";
import StatusBadge from "@/components/StatusBadge";
import ActivationRow from "@/components/ActivationRow";
import useVisibilityPoll from "@/hooks/useVisibilityPoll";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  ArrowUpRight,
  Upload,
  Sparkles,
  TrendingUp,
  Target,
  Send,
  XCircle,
  Brain,
  Activity,
  DollarSign,
  Filter,
  Undo2,
  Flame,
  AlertTriangle,
  Sunrise,
  Lightbulb,
  Stethoscope,
  Zap,
  Info,
  CheckCircle2,
  Copy,
  Loader2,
  Wand2,
  Circle,
  Rocket,
  Radio,
  Phone,
  Shield,
  Workflow,
  Trophy,
  ArrowRight,
  Bell,
  BellRing,
  BellOff,
  Share2,
  CalendarCheck,
} from "lucide-react";

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [strategist, setStrategist] = useState(null); // { mode, title, headline, lines, has_enough_data }
  const [strategistBusy, setStrategistBusy] = useState(null); // mode currently loading
  const [icpPreview, setIcpPreview] = useState(null); // {eligible, total_leads, to_remove_count, to_keep_count, criteria_lines, reason?}
  const [icpDialogOpen, setIcpDialogOpen] = useState(false);
  const [icpApplying, setIcpApplying] = useState(false);
  const [lastSnapshot, setLastSnapshot] = useState({ available: false });
  const [brief, setBrief] = useState(null); // { insights, brief_lines, hot_lead_ids, stuck_lead_ids, has_data }
  const [learning, setLearning] = useState(null); // { top_performing_tone, best_hook, best_icp, best_channel, insight, recommendation, has_enough_data, sample_size }
  const [autoStatus, setAutoStatus] = useState(null); // {autonomous_env_flag, execution_mode, job_counts}
  const [doctor, setDoctor] = useState(null); // { mode, title, data } for Sales Doctor output
  const [doctorBusy, setDoctorBusy] = useState(null); // one of "diagnose" | "prescribe" | "forecast" | "strategy"
  const [strategyQuestion, setStrategyQuestion] = useState("");
  const [actions, setActions] = useState(null); // { actions: [...], generated_at }
  const [impact, setImpact] = useState(null); // { reply_rate, reply_rate_delta, meetings, ... }
  const [sourceRoi, setSourceRoi] = useState(null); // { rows, best_source, worst_source, has_data }
  const [funnel, setFunnel] = useState(null); // { emails_sent_today, link_clicks_today, ..., trend, is_sample }
  const [insights, setInsights] = useState(null); // { insights: [...], is_sample, generated_at }
  const [ranking, setRanking] = useState(null); // { messages: [...], is_sample }
  // --- Command Center additions (AI-guided UX layer) ---
  // All state below is UI-only or derived from existing APIs; no new backend.
  const [queueHealth, setQueueHealth] = useState(null); // /api/system/health
  const [execMode, setExecMode] = useState(null); // "assisted" | "autonomous" | null
  const [execSaving, setExecSaving] = useState(false);
  const [recentJobs, setRecentJobs] = useState([]); // latest automation_jobs

  useEffect(() => {
    Promise.all([
      getStats(),
      listLeads(),
      icpOptimizeLastSnapshot().catch(() => ({ available: false })),
      getPipelineBrief().catch(() => null),
      getLearningInsights().catch(() => null),
      automationStatus().catch(() => null),
      getActionsToday().catch(() => null),
      getAiImpact(7).catch(() => null),
      getLeadSourceRoi().catch(() => null),
      getBookingFunnel().catch(() => null),
      getAiInsights().catch(() => null),
      getMessageRanking(30, 5).catch(() => null),
      getSettings().catch(() => null),
    ])
      .then(([s, l, snap, b, li, am, acts, imp, roi, fnl, ins, rnk, st]) => {
        setStats(s);
        setLeads(l.slice(0, 8));
        setLastSnapshot(snap);
        setBrief(b);
        setLearning(li);
        setAutoStatus(am);
        setActions(acts);
        setImpact(imp);
        setSourceRoi(roi);
        setFunnel(fnl);
        setInsights(ins);
        setRanking(rnk);
        // Fire-and-forget — populate book_score across all leads so the
        // Pipeline cards render the % chance-to-book chip on next load.
        scoreAllLeads().catch(() => null);
        if (st?.execution_mode) setExecMode(st.execution_mode);
      })
      .finally(() => setLoading(false));
  }, []);

  // Activation-aware poll cadence — when the workspace is empty
  // (funnel.is_empty) we poll every 10s so the user sees their first
  // data point land within seconds of hitting "Send". Once data arrives,
  // we fall back to the steady 60s cadence to conserve network calls.
  const _pollMs = funnel?.is_empty ? 10_000 : 60_000;
  useVisibilityPoll(() => {
    Promise.all([
      getPipelineBrief().catch(() => null),
      getLearningInsights().catch(() => null),
      automationStatus().catch(() => null),
      getActionsToday().catch(() => null),
      getAiImpact(7).catch(() => null),
      getBookingFunnel().catch(() => null),
      getAiInsights().catch(() => null),
      getMessageRanking(30, 5).catch(() => null),
    ]).then(([b, li, am, acts, imp, fnl, ins, rnk]) => {
      if (b) setBrief(b);
      if (li) setLearning(li);
      if (am) setAutoStatus(am);
      if (acts) setActions(acts);
      if (imp) setImpact(imp);
      if (fnl) setFunnel(fnl);
      if (ins) setInsights(ins);
      if (rnk) setRanking(rnk);
    });
  }, _pollMs);

  // System health poll — 30s cadence. Visibility-aware.
  useVisibilityPoll(async () => {
    try {
      const h = await getSystemHealth();
      setQueueHealth(h);
    } catch {
      setQueueHealth({ status: "unknown" });
    }
  }, 30_000);

  // Mini Burst Panel polls /api/jobs every 5s. Visibility-aware — stops
  // entirely while the tab is backgrounded (huge win: ~720 calls/hour saved
  // per idle tab).
  useVisibilityPoll(async () => {
    try {
      const res = await listJobs({ limit: 25 });
      setRecentJobs(res?.jobs || []);
    } catch (err) {
      // 404/401 on this endpoint should not spam console — log at debug only.
      if (typeof console !== "undefined" && console.debug) {
        console.debug("[dashboard] listJobs poll failed:", err?.response?.status || err?.message);
      }
    }
  }, 5_000);

  // Toggle autonomous mode — writes to settings.execution_mode (additive UX;
  // backend already consumes this flag via automation_engine).
  const toggleAutonomous = async () => {
    const next = execMode === "autonomous" ? "assisted" : "autonomous";
    setExecSaving(true);
    try {
      // PUT /settings is full-replace with required product_name/description
      // etc — must merge existing before sending.
      const existing = (await getSettings().catch(() => null)) || {};
      await saveSettings({
        product_name: existing.product_name || "",
        product_description: existing.product_description || "",
        pain_point: existing.pain_point || "",
        target_icp: existing.target_icp || "",
        problems_solved: existing.problems_solved || [],
        differentiation: existing.differentiation || [],
        proof_points: existing.proof_points || [],
        industry: existing.industry || [],
        company_size: existing.company_size || "",
        decision_maker_roles: existing.decision_maker_roles || [],
        pain_signals: existing.pain_signals || [],
        triggers: existing.triggers || [],
        disqualifiers: existing.disqualifiers || [],
        preferred_language: existing.preferred_language || "en",
        calendar_link: existing.calendar_link || "",
        sender_name: existing.sender_name || "",
        sender_email: existing.sender_email || "",
        reply_to_email: existing.reply_to_email || "",
        execution_mode: next,
      });
      setExecMode(next);
      toast.success(
        next === "autonomous"
          ? "Autonomous Mode ON — AI will execute outreach automatically"
          : "Assisted Mode — each action needs your approval",
      );
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d) => d?.msg || String(d)).join("; ")
        : typeof detail === "string"
          ? detail
          : err?.message || "Unknown error";
      toast.error("Could not save mode", { description: msg });
    } finally {
      setExecSaving(false);
    }
  };

  // ============ Command Center derived state ============
  // High-intent = intent_label "High" OR replied positively OR brief.hot_lead_ids.
  // We bias towards brief.hot_lead_ids since the backend already ranks them.
  const highIntentLeads = useMemo(() => {
    const hotIds = new Set(brief?.hot_lead_ids || []);
    const scored = leads
      .map((l) => {
        const intentHigh = (l.intent_label || "").toLowerCase() === "high";
        const hot = hotIds.has(l.id);
        const replied = (l.last_reply_sentiment || "").toLowerCase() === "positive";
        const score =
          (l.conversion_score ?? 0) +
          (hot ? 20 : 0) +
          (intentHigh ? 15 : 0) +
          (replied ? 10 : 0);
        return { lead: l, hot, intentHigh, replied, score };
      })
      .filter((x) => x.hot || x.intentHigh || x.replied || x.score >= 60)
      .sort((a, b) => b.score - a.score);
    return scored.slice(0, 5);
  }, [leads, brief]);

  const planCounts = useMemo(() => {
    const high = highIntentLeads.length;
    const warm = leads.filter((l) =>
      ["FOLLOWUP_PENDING", "EMAILED"].includes((l.lifecycle || "").toUpperCase()),
    ).length;
    const meetings = leads.filter((l) =>
      (l.lifecycle || "").toUpperCase() === "MEETING_BOOKED",
    ).length;
    return { high, warm, meetings };
  }, [leads, highIntentLeads]);

  // Live Intent Feed — derive "events" from the existing leads + brief data.
  // Each event is a tuple { id, lead, kind, headline }. No new API.
  const liveFeed = useMemo(() => {
    const events = [];
    const seen = new Set();
    (brief?.hot_lead_ids || []).forEach((id) => {
      const lead = leads.find((l) => l.id === id);
      if (!lead || seen.has(id)) return;
      seen.add(id);
      events.push({
        id: `hot-${id}`,
        lead,
        kind: "hot",
        headline:
          lead.recent_signal ||
          lead.last_reply_text ||
          lead.priority_reason ||
          "Flagged as high intent by AI",
      });
    });
    leads
      .filter((l) => (l.last_reply_sentiment || "").toLowerCase() === "positive")
      .forEach((l) => {
        if (seen.has(l.id)) return;
        seen.add(l.id);
        events.push({
          id: `reply-${l.id}`,
          lead: l,
          kind: "reply",
          headline:
            (l.last_reply_text && l.last_reply_text.length > 90
              ? `${l.last_reply_text.slice(0, 90)}…`
              : l.last_reply_text) || "Replied positively",
        });
      });
    leads
      .filter((l) => (l.lifecycle || "").toUpperCase() === "MEETING_BOOKED")
      .forEach((l) => {
        if (seen.has(l.id)) return;
        seen.add(l.id);
        events.push({
          id: `mtg-${l.id}`,
          lead: l,
          kind: "meeting",
          headline: "Meeting booked — on your calendar",
        });
      });
    return events.slice(0, 8);
  }, [leads, brief]);

  const runDoctor = async (mode) => {
    setDoctorBusy(mode);
    try {
      let data = null;
      let title = "";
      if (mode === "diagnose") {
        data = await diagnoseSales();
        title = "Sales Diagnosis";
      } else if (mode === "prescribe") {
        data = await prescribeActions();
        title = "Sales Prescription";
      } else if (mode === "forecast") {
        data = await forecastRevenue();
        title = "30-Day Revenue Forecast";
      } else if (mode === "strategy") {
        const q = (strategyQuestion || "").trim();
        if (!q) {
          toast.error("Enter a question first");
          setDoctorBusy(null);
          return;
        }
        data = await strategyAdvisor(q);
        title = "Strategic Advisor";
      }
      setDoctor({ mode, title, data });
    } catch (err) {
      toast.error("Sales Doctor failed", {
        description: err?.response?.data?.detail || err.message,
      });
    } finally {
      setDoctorBusy(null);
    }
  };

  const refreshAfterIcpAction = async () => {
    try {
      const [s, l, snap, b] = await Promise.all([
        getStats(),
        listLeads(),
        icpOptimizeLastSnapshot().catch(() => ({ available: false })),
        getPipelineBrief().catch(() => null),
      ]);
      setStats(s);
      setLeads(l.slice(0, 8));
      setLastSnapshot(snap);
      setBrief(b);
    } catch (error) {
      if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.error("Dashboard refresh failed:", error);
      }
      toast.error("Could not refresh dashboard", {
        description: error?.response?.data?.detail || error.message,
      });
    }
  };

  const onPreviewIcpApply = async () => {
    try {
      const preview = await icpOptimizePreview();
      setIcpPreview(preview);
      setIcpDialogOpen(true);
    } catch (err) {
      toast.error("Preview failed", {
        description: err?.response?.data?.detail || err.message,
      });
    }
  };

  const onConfirmIcpApply = async () => {
    setIcpApplying(true);
    try {
      const res = await icpOptimizeApply();
      toast.success(
        `Pipeline optimized — ${res.applied} low-fit lead${res.applied === 1 ? "" : "s"} removed`,
        { description: "You can undo this from the Command Center." },
      );
      setIcpDialogOpen(false);
      await refreshAfterIcpAction();
    } catch (err) {
      toast.error("Apply failed", {
        description: err?.response?.data?.detail || err.message,
      });
    } finally {
      setIcpApplying(false);
    }
  };

  const onUndoIcp = async () => {
    try {
      const res = await icpOptimizeUndo();
      toast.success(`Restored ${res.restored} lead${res.restored === 1 ? "" : "s"}`);
      await refreshAfterIcpAction();
    } catch (err) {
      toast.error("Undo failed", {
        description: err?.response?.data?.detail || err.message,
      });
    }
  };

  const runMode = async (mode) => {
    setStrategistBusy(mode);
    setStrategist(null);
    const start = Date.now();
    try {
      const res = await runStrategist(mode);
      const elapsed = Date.now() - start;
      if (elapsed < 500) await new Promise((r) => setTimeout(r, 500 - elapsed));
      setStrategist(res);
    } catch (err) {
      setStrategist({
        mode,
        title: "Something went wrong",
        headline: err?.response?.data?.detail || err.message,
        lines: [],
        has_enough_data: false,
      });
    } finally {
      setStrategistBusy(null);
    }
  };

  return (
    <div className="px-6 md:px-10 py-8 space-y-8" data-testid="dashboard-page">
      {/* ============================================================ */}
      {/* SECTION 1 — COMMAND CENTER HEADER (premium hero)             */}
      {/* ============================================================ */}
      <CommandCenterHeader
        user={user}
        stats={stats}
        funnel={funnel}
        execMode={execMode}
        toggleAutonomous={toggleAutonomous}
        execSaving={execSaving}
        highIntentCount={highIntentLeads.length}
        leadsReady={leads.length > 0}
      />

      {/* ACTIVATION + CELEBRATION ROW — surfaces only at empty / first
          milestones. Quiet days return null for a clean dashboard. */}
      <ActivationRow funnel={funnel} leadsCount={leads.length} />

      {/* ============================================================ */}
      {/* SECTION 2 — KPI STRIP (4 hero + 2 secondary)                 */}
      {/* ============================================================ */}
      <HeroKpiStrip stats={stats} loading={loading} />

      {/* ============================================================ */}
      {/* SECTION 3 — BOOKING FUNNEL (centerpiece, hero component)     */}
      {/* ============================================================ */}
      <BookingFunnelCard funnel={funnel} loading={loading} />

      {/* ============================================================ */}
      {/* SECTION 4 — AI INSIGHTS (left) + TOP MESSAGES (right)        */}
      {/* ============================================================ */}
      <section
        className="grid grid-cols-1 lg:grid-cols-5 gap-4"
        data-testid="insights-row"
      >
        <div className="lg:col-span-3">
          <AiInsightsCard insights={insights} loading={loading} />
        </div>
        <div className="lg:col-span-2">
          <TopMessagesCard ranking={ranking} loading={loading} />
        </div>
      </section>

      {/* ============================================================ */}
      {/* SECTION 5 — PIPELINE PREVIEW (premium card list + intent)    */}
      {/* ============================================================ */}
      <PrimaryActionBanner
        count={highIntentLeads.length}
        leadsReady={leads.length > 0}
        execMode={execMode}
        toggleAutonomous={toggleAutonomous}
        execSaving={execSaving}
      />

      {highIntentLeads.length > 0 && (
        <HighIntentStoryCards highIntentLeads={highIntentLeads} />
      )}

      <PipelinePreviewCards leads={leads} />

      {/* ============================================================ */}
      {/* SECTION 6 — SECONDARY ANALYTICS (collapsed-feeling stack)    */}
      {/* ============================================================ */}

      {/* Outcome strip — small celebratory cards (only when there's data) */}
      {leads.length > 0 && (
        <div
          className="grid grid-cols-1 md:grid-cols-3 gap-3"
          data-testid="outcome-strip"
        >
          <MeetingsBookedTodayCard leads={leads} />
          <ShareDemoCard />
          <PipelineGeneratedCard impact={impact} />
        </div>
      )}

      <MiniBurstPanel jobs={recentJobs} />

      {(highIntentLeads.length > 0 ||
        planCounts.warm > 0 ||
        planCounts.meetings > 0) && (
        <section
          className="grid grid-cols-1 lg:grid-cols-3 gap-4"
          data-testid="command-center-row"
        >
          <div className="lg:col-span-1">
            <TodayPlanCard counts={planCounts} />
          </div>
          <div className="lg:col-span-2">
            <LiveIntentFeed events={liveFeed} />
          </div>
        </section>
      )}

      {brief && <DailySalesBrief brief={brief} />}

      <section
        className="grid grid-cols-1 lg:grid-cols-5 gap-4"
        data-testid="actions-impact-row"
      >
        <div className="lg:col-span-3">
          <TopActionsToday actions={actions} />
        </div>
        <div className="lg:col-span-2">
          <AiImpactPanel impact={impact} />
        </div>
      </section>

      <LeadSourceRoiCard roi={sourceRoi} loading={loading} />

      <SalesCommandCenter
        strategist={strategist}
        busy={strategistBusy}
        onRun={runMode}
        totalLeads={stats?.total ?? 0}
        lastSnapshot={lastSnapshot}
        onPreviewApply={onPreviewIcpApply}
        onUndo={onUndoIcp}
        brief={brief}
      />

      {learning && (
        <AiSalesIntelligence learning={learning} autoStatus={autoStatus} />
      )}

      <SalesDoctorPanel
        doctor={doctor}
        busy={doctorBusy}
        onRun={runDoctor}
        strategyQuestion={strategyQuestion}
        setStrategyQuestion={setStrategyQuestion}
      />

      <ActivationChecklist leads={leads} stats={stats} />
      <TrialProgressCard />
      <SystemHealthBar health={queueHealth} />

      <AlertDialog open={icpDialogOpen} onOpenChange={setIcpDialogOpen}>
        <AlertDialogContent
          className="max-w-lg"
          data-testid="icp-apply-dialog"
        >
          <AlertDialogHeader>
            <AlertDialogTitle>Apply ICP optimization?</AlertDialogTitle>
            <AlertDialogDescription className="pt-1">
              Low-fit leads will be flagged as rejected. This action is{" "}
              <strong className="text-slate-900">reversible</strong> via the
              "Undo" button in the Command Center.
            </AlertDialogDescription>
          </AlertDialogHeader>

          {icpPreview && (
            <div className="space-y-3 text-[13px]">
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-md border border-green-200 bg-green-50 px-3 py-2.5">
                  <div className="label-caps !text-[10px] text-green-800">
                    Keep
                  </div>
                  <div
                    className="mt-0.5 text-2xl font-bold text-green-900"
                    data-testid="icp-keep-count"
                  >
                    {icpPreview.to_keep_count}
                  </div>
                </div>
                <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2.5">
                  <div className="label-caps !text-[10px] text-red-700">
                    Remove
                  </div>
                  <div
                    className="mt-0.5 text-2xl font-bold text-red-800"
                    data-testid="icp-remove-count"
                  >
                    {icpPreview.to_remove_count}
                  </div>
                </div>
              </div>
              <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2.5">
                <div className="label-caps !text-[10px] text-slate-500 mb-1">
                  Criteria applied
                </div>
                <ul className="space-y-1">
                  {icpPreview.criteria_lines?.map((c, i) => (
                    <li
                      key={`crit-${i}-${(c || "").slice(0, 40)}`}
                      className="text-[12.5px] text-slate-700 leading-relaxed flex gap-2"
                    >
                      <span className="text-[#002FA7] shrink-0">•</span>
                      <span>{c}</span>
                    </li>
                  ))}
                </ul>
              </div>
              {icpPreview.to_remove_count > 0 && icpPreview.to_remove && (
                <div className="max-h-[140px] overflow-y-auto rounded-md border border-slate-200 px-3 py-2">
                  <div className="label-caps !text-[10px] text-slate-500 mb-1">
                    Sample of leads being removed
                  </div>
                  <ul className="space-y-0.5" data-testid="icp-remove-list">
                    {icpPreview.to_remove.slice(0, 8).map((l) => (
                      <li
                        key={l.id}
                        className="text-[12px] text-slate-700"
                      >
                        <span className="font-medium">{l.name || "(unnamed)"}</span>
                        {l.company && <span> · {l.company}</span>}
                        <span className="text-slate-400"> — {l.reason}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          <AlertDialogFooter>
            <AlertDialogCancel data-testid="icp-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={onConfirmIcpApply}
              disabled={icpApplying || icpPreview?.to_remove_count === 0}
              data-testid="icp-confirm-btn"
              className="bg-[#002FA7] hover:bg-[#002072]"
            >
              {icpApplying ? "Applying…" : "Apply changes"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ============================================================================
// $10M SaaS Layer — Premium Command Center Header + Hero KPI Strip
// Designed for CEOs, not developers. Goal: in 5 seconds the user knows
//   1) is the AI working right now
//   2) what's the headline number today
//   3) what to do next
// ============================================================================

const HOUR_GREETINGS = [
  { from: 5, to: 12, text: "Good morning" },
  { from: 12, to: 17, text: "Good afternoon" },
  { from: 17, to: 22, text: "Good evening" },
];

function _greeting(name) {
  const h = new Date().getHours();
  const found = HOUR_GREETINGS.find((g) => h >= g.from && h < g.to);
  const base = found ? found.text : "Hello";
  if (!name) return base;
  const first = String(name).split(/[\s@]/)[0];
  return `${base}, ${first}`;
}

function CommandCenterHeader({
  user,
  stats,
  funnel,
  execMode,
  toggleAutonomous,
  execSaving,
  highIntentCount,
  leadsReady,
}) {
  const isAuto = execMode === "autonomous";
  const meetingsToday = Number(funnel?.meetings_booked_today || 0);
  const emailsToday = Number(funnel?.emails_sent_today || 0);
  const totalSent = stats?.emailed_total ?? stats?.sent ?? 0;
  // Status line is one declarative narrative, not a stat dump
  let statusLine;
  if (highIntentCount > 0) {
    statusLine = (
      <>
        <span className="font-bold text-orange-300">{highIntentCount}</span>{" "}
        high-intent {highIntentCount === 1 ? "lead is" : "leads are"} ready to engage
      </>
    );
  } else if (meetingsToday > 0) {
    statusLine = (
      <>
        AI booked{" "}
        <span className="font-bold text-emerald-300">{meetingsToday}</span>{" "}
        {meetingsToday === 1 ? "meeting" : "meetings"} today — pipeline is compounding
      </>
    );
  } else if (emailsToday > 0) {
    statusLine = (
      <>
        AI dispatched{" "}
        <span className="font-bold text-indigo-200">{emailsToday}</span>{" "}
        {emailsToday === 1 ? "email" : "emails"} today — watching for replies
      </>
    );
  } else if (leadsReady) {
    statusLine = "AI is armed. Engage your warmest leads to start the loop.";
  } else {
    statusLine = "Upload leads to start your AI sales engine.";
  }

  return (
    <section
      data-testid="command-center-header"
      data-autonomous-mode={isAuto ? "on" : "off"}
      className="relative overflow-hidden rounded-3xl bg-[#0B1020] text-white shadow-[0_30px_80px_-30px_rgba(2,6,23,0.6)]"
    >
      {/* Ambient layered gradients — depth, not noise */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,rgba(99,102,241,0.35),transparent_55%),radial-gradient(ellipse_at_bottom_right,rgba(236,72,153,0.25),transparent_50%)]"
      />
      {/* Grain texture — subtle SaaS premium feel */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 opacity-[0.06] mix-blend-overlay"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 200 200'><filter id='n'><feTurbulence baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>\")",
        }}
      />
      <div className="relative px-6 sm:px-10 py-8 sm:py-10 grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10 items-center">
        {/* Left: Greeting + Status line */}
        <div className="lg:col-span-7 min-w-0">
          <div className="flex items-center gap-2 text-[10.5px] font-bold uppercase tracking-[0.22em] text-indigo-200/80">
            <span className="relative inline-flex h-2 w-2">
              <span className="absolute inset-0 rounded-full bg-emerald-400 animate-ping opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
            </span>
            AI Sales Command Center · Live
          </div>
          <h1
            data-testid="command-center-greeting"
            className="mt-3 font-display text-3xl sm:text-4xl lg:text-[42px] font-extrabold tracking-tight leading-[1.05]"
          >
            {_greeting(user?.email || user?.name)}.
          </h1>
          <p
            data-testid="command-center-status"
            className="mt-3 text-[15px] sm:text-[16px] text-slate-200/90 max-w-2xl leading-relaxed"
          >
            {statusLine}
          </p>

          {/* Compact secondary stats — tabular nums, monospaced energy */}
          <div className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-2 text-[12.5px]">
            <div className="flex items-center gap-1.5">
              <span className="text-slate-400">Today</span>
              <span className="font-bold text-white tabular-nums">
                {emailsToday}
              </span>
              <span className="text-slate-400">sent</span>
            </div>
            <div className="h-3 w-px bg-white/10" />
            <div className="flex items-center gap-1.5">
              <span className="text-slate-400">Today</span>
              <span className="font-bold text-emerald-300 tabular-nums">
                {meetingsToday}
              </span>
              <span className="text-slate-400">booked</span>
            </div>
            <div className="h-3 w-px bg-white/10" />
            <div className="flex items-center gap-1.5">
              <span className="text-slate-400">Lifetime</span>
              <span className="font-bold text-white tabular-nums">
                {totalSent}
              </span>
              <span className="text-slate-400">outreach</span>
            </div>
          </div>
        </div>

        {/* Right: Autonomous toggle + Quick CTAs (preserves hero-* testids) */}
        <div className="lg:col-span-5 flex flex-col gap-3 sm:items-end">
          <button
            type="button"
            onClick={toggleAutonomous}
            disabled={execSaving}
            data-testid="command-center-autonomous-toggle"
            data-autonomous-mode={isAuto ? "on" : "off"}
            className={`group inline-flex items-center gap-2.5 rounded-full px-4 py-2 text-[12.5px] font-semibold transition-all border backdrop-blur-md self-start sm:self-auto ${
              isAuto
                ? "bg-emerald-400/15 border-emerald-300/50 text-emerald-50 hover:bg-emerald-400/25"
                : "bg-white/8 border-white/20 text-white/90 hover:bg-white/15"
            } disabled:opacity-60`}
            title={
              isAuto
                ? "Autonomous Mode — AI is executing outreach automatically"
                : "Assisted Mode — each action requires your approval"
            }
          >
            <span
              className={`relative inline-flex h-2 w-2 ${
                isAuto ? "" : "opacity-50"
              }`}
            >
              {isAuto && (
                <span className="absolute inset-0 rounded-full bg-emerald-300 animate-ping opacity-75" />
              )}
              <span
                className={`relative inline-flex rounded-full h-2 w-2 ${
                  isAuto ? "bg-emerald-300" : "bg-white/60"
                }`}
              />
            </span>
            <span className="font-mono uppercase tracking-wider text-[11px]">
              {isAuto ? "Autonomous" : "Assisted"}
            </span>
            <span className="text-white/40">·</span>
            <span className="text-[11px]">Tap to {isAuto ? "pause" : "go live"}</span>
          </button>

          <div className="flex items-center gap-2.5 self-start sm:self-auto">
            <Link to="/leads">
              <Button
                variant="ghost"
                data-testid="hero-upload-btn"
                className="h-11 rounded-xl bg-white/8 hover:bg-white/15 border border-white/15 text-white"
              >
                <Upload className="h-4 w-4 mr-2" />
                Upload leads
              </Button>
            </Link>
            <Link to="/setup">
              <Button
                data-testid="hero-setup-btn"
                className="h-11 rounded-xl bg-white text-slate-900 hover:bg-white/90 font-semibold shadow-lg"
              >
                <Sparkles className="h-4 w-4 mr-2" />
                Configure
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

// ============================================================================
// HeroKpiStrip — 4 large primary KPI cards + 2 secondary chips below.
// Preserves all 6 stats-grid test IDs (stat-total, stat-qualified, stat-sent,
// stat-reply-rate, stat-meetings-booked, stat-rejected).
// ============================================================================

function KpiCard({ testId, label, value, sub, accent, icon: Icon, loading }) {
  const display = useAnimatedCount(
    typeof value === "number" ? value : 0,
    { durationMs: 800 },
  );
  const displayed = loading ? "—" : (typeof value === "string" ? value : display);
  return (
    <div
      data-testid={testId}
      className="group relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-5 sm:p-6 transition-all hover:shadow-[0_20px_50px_-25px_rgba(15,23,42,0.25)] hover:-translate-y-0.5"
    >
      {/* Top accent bar */}
      <div
        aria-hidden="true"
        className="absolute inset-x-5 top-0 h-1 rounded-b-full"
        style={{ background: accent }}
      />
      <div className="flex items-start justify-between">
        <div className="text-[10.5px] font-bold uppercase tracking-[0.18em] text-slate-500">
          {label}
        </div>
        {Icon && (
          <span
            className="inline-flex h-8 w-8 items-center justify-center rounded-lg"
            style={{ backgroundColor: `${accent}1A`, color: accent }}
          >
            <Icon className="h-4 w-4" />
          </span>
        )}
      </div>
      <div
        className="mt-3 font-display text-4xl sm:text-5xl font-extrabold tracking-tight text-slate-900 tabular-nums"
        data-testid={`${testId}-value`}
      >
        {displayed}
      </div>
      {sub && (
        <div className="mt-1.5 text-[12px] text-slate-500">{sub}</div>
      )}
    </div>
  );
}

function MicroStat({ testId, label, value, hint, accent }) {
  return (
    <div
      data-testid={testId}
      className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3"
    >
      <span
        className="h-2 w-2 rounded-full shrink-0"
        style={{ backgroundColor: accent }}
      />
      <div className="min-w-0 flex-1">
        <div className="text-[10.5px] font-bold uppercase tracking-wider text-slate-500">
          {label}
        </div>
        <div className="text-[12.5px] text-slate-700 truncate">{hint}</div>
      </div>
      <div
        className="font-display text-xl font-extrabold tabular-nums text-slate-900"
        data-testid={`${testId}-value`}
      >
        {value}
      </div>
    </div>
  );
}

function HeroKpiStrip({ stats, loading }) {
  const total = stats?.total ?? 0;
  const sent = stats?.emailed_total ?? stats?.sent ?? 0;
  const replyRate = stats?.reply_rate ?? 0;
  const meetings = stats?.meetings_booked ?? 0;
  const qualified = stats?.qualified ?? 0;
  const rejected = (stats?.rejected ?? 0) + (stats?.invalid ?? 0);

  return (
    <section data-testid="hero-kpi-strip" className="space-y-3">
      <div
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4"
        data-testid="stats-grid"
      >
        <KpiCard
          testId="stat-meetings-booked"
          label="Meetings booked"
          value={meetings}
          sub="Pipeline outcome"
          accent="#10B981"
          icon={CalendarCheck}
          loading={loading}
        />
        <KpiCard
          testId="stat-reply-rate"
          label="Reply rate"
          value={loading ? "—" : `${replyRate}%`}
          sub="Healthy ≥ 8%"
          accent="#7C3AED"
          icon={Activity}
          loading={loading}
        />
        <KpiCard
          testId="stat-sent"
          label="Emails sent"
          value={sent}
          sub="Lifetime via Resend"
          accent="#002FA7"
          icon={Send}
          loading={loading}
        />
        <KpiCard
          testId="stat-total"
          label="Total leads"
          value={total}
          sub="In your workspace"
          accent="#0EA5E9"
          icon={Target}
          loading={loading}
        />
      </div>

      {/* Secondary KPIs — quieter, single line */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="kpi-strip-secondary">
        <MicroStat
          testId="stat-qualified"
          label="Qualified"
          value={loading ? "—" : qualified}
          hint={`Avg ICP ${stats?.avg_icp ?? 0} · Above 60`}
          accent="#16A34A"
        />
        <MicroStat
          testId="stat-rejected"
          label="Rejected"
          value={loading ? "—" : rejected}
          hint="Outside ICP — auto-flagged"
          accent="#94A3B8"
        />
      </div>
    </section>
  );
}

// ============================================================================
// PipelinePreviewCards — replaces the dense recent-leads table with premium
// card rows. Preserves all `recent-row-{id}`, `view-all-leads`,
// `empty-leads`, `empty-upload-btn` test IDs.
// ============================================================================

function PipelinePreviewCards({ leads }) {
  return (
    <section
      data-testid="pipeline-preview"
      className="rounded-3xl border border-slate-200 bg-white"
    >
      <div className="flex items-center justify-between gap-3 px-6 py-5 border-b border-slate-100">
        <div className="flex items-center gap-3">
          <span className="inline-flex h-9 w-9 rounded-xl bg-gradient-to-br from-indigo-500 to-blue-500 items-center justify-center shadow-md shadow-indigo-500/25">
            <Workflow className="h-4 w-4 text-white" />
          </span>
          <div>
            <div className="text-[10.5px] font-bold uppercase tracking-[0.18em] text-slate-500">
              Pipeline preview
            </div>
            <h3 className="font-display text-lg font-bold tracking-tight text-slate-900">
              Recent leads
            </h3>
          </div>
        </div>
        <Link
          to="/leads"
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-[13px] font-semibold text-[#002FA7] hover:bg-indigo-50 transition-colors"
          data-testid="view-all-leads"
        >
          View all <ArrowUpRight className="h-3.5 w-3.5" />
        </Link>
      </div>

      {leads.length === 0 ? (
        <div
          className="m-6 p-10 rounded-2xl border border-dashed border-slate-300 text-center"
          data-testid="empty-leads"
        >
          <Target className="h-6 w-6 text-slate-400 mx-auto" />
          <p className="mt-3 text-sm font-medium text-slate-700">
            No leads yet
          </p>
          <p className="mt-1 text-xs text-slate-500">
            Upload a CSV to get started.
          </p>
          <Link to="/leads">
            <Button
              size="sm"
              className="mt-4 bg-[#002FA7] hover:bg-[#002072]"
              data-testid="empty-upload-btn"
            >
              Upload CSV
            </Button>
          </Link>
        </div>
      ) : (
        <div className="divide-y divide-slate-100">
          {leads.map((l) => (
            <Link
              key={l.id}
              to={`/leads/${l.id}`}
              data-testid={`recent-row-${l.id}`}
              className="group flex items-center gap-4 px-6 py-4 hover:bg-slate-50/60 transition-colors"
            >
              <div className="h-9 w-9 rounded-full bg-gradient-to-br from-slate-200 to-slate-100 grid place-items-center shrink-0">
                <span className="text-[13px] font-bold text-slate-600">
                  {(l.name || "?").trim().charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-slate-900 truncate">
                  {l.name || "—"}
                </div>
                <div className="text-[12px] text-slate-500 truncate">
                  {l.title ? `${l.title} · ` : ""}
                  {l.company || "—"}
                </div>
              </div>
              <div className="hidden sm:flex flex-col items-end gap-0.5 shrink-0 w-20">
                <div className="text-[10.5px] font-bold uppercase tracking-wider text-slate-400">
                  ICP
                </div>
                <div className="font-mono text-[14px] font-bold tabular-nums text-slate-800">
                  {l.icp_score ?? "—"}
                </div>
              </div>
              <div className="shrink-0">
                <StatusBadge status={l.status} />
              </div>
              <ArrowUpRight className="h-4 w-4 text-slate-300 group-hover:text-[#002FA7] group-hover:translate-x-0.5 transition-transform shrink-0" />
            </Link>
          ))}
        </div>
      )}
    </section>
  );
}

const STRATEGIST_MODES = [
  {
    key: "icp_advisor",
    label: "Improve my ICP",
    sub: "Who to target",
    Icon: Target,
    color: "#002FA7",
  },
  {
    key: "performance_diagnosis",
    label: "Why reply rate?",
    sub: "What to fix",
    Icon: Activity,
    color: "#DC2626",
  },
  {
    key: "revenue_insight",
    label: "Revenue insight",
    sub: "Activity → revenue",
    Icon: DollarSign,
    color: "#16A34A",
  },
];

function SalesCommandCenter({
  strategist,
  busy,
  onRun,
  totalLeads,
  lastSnapshot,
  onPreviewApply,
  onUndo,
  brief,
}) {
  const showApplyButtons = strategist?.mode === "icp_advisor" && !busy;
  const canApply = (totalLeads || 0) >= 5;
  const pipelineChips = useMemo(
    () =>
      brief?.has_data
        ? [
            {
              key: "followup",
              label: "need follow-up today",
              value: brief.insights?.need_followup_today ?? 0,
              Icon: Send,
              cls: "bg-blue-50 text-[#002FA7] border-blue-200",
              testid: "pipeline-followup",
            },
            {
              key: "high-intent",
              label: "high-intent",
              value: brief.insights?.high_intent ?? 0,
              Icon: Flame,
              cls: "bg-emerald-50 text-emerald-800 border-emerald-200",
              testid: "pipeline-high-intent",
            },
            {
              key: "stuck",
              label: "stuck (>3d)",
              value: brief.insights?.stuck ?? 0,
              Icon: AlertTriangle,
              cls: "bg-amber-50 text-amber-800 border-amber-200",
              testid: "pipeline-stuck",
            },
          ]
        : [],
    [brief],
  );
  // Revenue-insight structured rows — memoize the 3-row filter so the array
  // reference stays stable across renders when strategist payload is unchanged.
  const revenueRows = useMemo(
    () =>
      [
        {
          label: "Insight",
          value: strategist?.insight,
          cls: "bg-blue-50 text-[#002FA7] border-blue-200",
          testid: "revenue-insight",
        },
        {
          label: "Cause",
          value: strategist?.cause,
          cls: "bg-amber-50 text-amber-800 border-amber-200",
          testid: "revenue-cause",
        },
        {
          label: "Action",
          value: strategist?.action,
          cls: "bg-emerald-50 text-emerald-800 border-emerald-200",
          testid: "revenue-action",
        },
      ].filter((row) => row.value),
    [strategist],
  );
  return (
    <section
      className="rounded-2xl border border-[#B8C9FF] bg-gradient-to-br from-[#F0F5FF] via-[#F6F9FF] to-white p-6 shadow-[0_1px_2px_rgba(0,47,167,0.04)]"
      data-testid="sales-command-center"
    >
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-xl bg-[#002FA7] text-white grid place-items-center shrink-0 shadow-sm">
          <Brain className="h-4.5 w-4.5" strokeWidth={2.25} />
        </div>
        <div>
          <div className="font-display text-[17px] font-bold tracking-tight text-slate-900">
            AI Sales Command Center
          </div>
          <div className="text-[12px] text-slate-500 leading-relaxed">
            Workspace-level coaching across targeting, performance, and revenue.
          </div>
        </div>
      </div>

      {/* Pipeline Manager (light) — live counts */}
      {pipelineChips.length > 0 && (
        <div
          className="mt-4 flex flex-wrap items-center gap-2"
          data-testid="pipeline-insights"
        >
          {pipelineChips.map(({ key, label, value, Icon, cls, testid }) => (
            <span
              key={key}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11.5px] font-semibold border ${cls}`}
              data-testid={testid}
            >
              <Icon className="h-3 w-3" />
              <span className="font-bold">{value}</span>
              <span className="font-medium opacity-80">{label}</span>
            </span>
          ))}
        </div>
      )}

      <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-3">
        {STRATEGIST_MODES.map(({ key, label, sub, Icon, color }) => {
          const active = strategist?.mode === key;
          const thisBusy = busy === key;
          return (
            <button
              key={key}
              onClick={() => onRun(key)}
              disabled={!!busy}
              data-testid={`strategist-${key}`}
              className={`group relative rounded-xl border bg-white/80 text-left px-4 py-3 transition-all ${
                active
                  ? "border-[#002FA7] ring-1 ring-[#002FA7]/20"
                  : "border-slate-200 hover:border-[#B8C9FF] hover:bg-white"
              } ${thisBusy ? "opacity-70" : ""}`}
            >
              <div className="flex items-center gap-2">
                <Icon className="h-4 w-4" style={{ color }} />
                <span className="text-[13px] font-semibold text-slate-900">
                  {thisBusy ? "Analyzing…" : label}
                </span>
              </div>
              <div className="mt-0.5 text-[11.5px] text-slate-500">{sub}</div>
            </button>
          );
        })}
      </div>

      {strategist && !busy && (
        <div
          className="mt-5 rounded-xl border border-white/80 bg-white/90 px-5 py-4 shadow-[0_1px_2px_rgba(0,47,167,0.04)] animate-brain-fade-in"
          data-testid="strategist-result"
        >
          <div className="flex items-center gap-2 mb-2">
            <div className="label-caps !text-[10px] text-[#002FA7]">
              {strategist.title}
            </div>
            {strategist.has_enough_data === false && (
              <span className="text-[10.5px] font-semibold text-amber-700 uppercase tracking-wider">
                · limited data
              </span>
            )}
          </div>
          <p className="text-[14.5px] font-semibold text-slate-900 leading-relaxed">
            {strategist.headline}
          </p>

          {/* Revenue Insight — structured Insight → Cause → Action */}
          {strategist.mode === "revenue_insight" &&
            (strategist.insight || strategist.cause || strategist.action) && (
              <div
                className="mt-4 grid gap-2.5"
                data-testid="revenue-structured"
              >
                {revenueRows.map((row) => (
                  <div
                    key={row.label}
                    className="flex items-start gap-3"
                    data-testid={row.testid}
                  >
                    <span
                      className={`inline-flex items-center justify-center h-6 px-2.5 rounded-md text-[10.5px] font-bold uppercase tracking-wider border shrink-0 ${row.cls}`}
                    >
                      {row.label}
                    </span>
                    <p className="text-[13px] text-slate-700 leading-relaxed pt-0.5">
                      {row.value}
                    </p>
                  </div>
                ))}
              </div>
            )}

          {/* ICP Advisor — structured best/weak segments + recommendation */}
          {strategist.mode === "icp_advisor" &&
            (strategist.best_segments?.length ||
              strategist.weak_segments?.length ||
              strategist.recommendation) && (
              <div
                className="mt-4 space-y-3"
                data-testid="icp-structured"
              >
                {strategist.best_segments?.length > 0 && (
                  <div data-testid="icp-best-segments">
                    <div className="label-caps !text-[10px] text-emerald-700">
                      Best-performing segments
                    </div>
                    <div className="mt-1.5 flex flex-wrap gap-1.5">
                      {strategist.best_segments.map((seg, i) => (
                        <span
                          key={`best-${i}-${(seg || "").slice(0, 40)}`}
                          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[12px] font-semibold border bg-emerald-50 text-emerald-800 border-emerald-200"
                        >
                          {seg}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {strategist.weak_segments?.length > 0 && (
                  <div data-testid="icp-weak-segments">
                    <div className="label-caps !text-[10px] text-slate-500">
                      Weak segments
                    </div>
                    <div className="mt-1.5 flex flex-wrap gap-1.5">
                      {strategist.weak_segments.map((seg, i) => (
                        <span
                          key={`weak-${i}-${(seg || "").slice(0, 40)}`}
                          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[12px] font-semibold border bg-slate-100 text-slate-600 border-slate-200"
                        >
                          {seg}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {strategist.recommendation && (
                  <div
                    className="rounded-md border border-[#B8C9FF] bg-[#F0F5FF] px-3 py-2.5"
                    data-testid="icp-recommendation"
                  >
                    <div className="label-caps !text-[10px] text-[#002FA7]">
                      Recommendation
                    </div>
                    <p className="mt-1 text-[13px] text-slate-800 leading-relaxed">
                      {strategist.recommendation}
                    </p>
                  </div>
                )}
              </div>
            )}

          {/* Performance Diagnosis + fallback line list for backward compat */}
          {(strategist.mode === "performance_diagnosis" ||
            (!["revenue_insight", "icp_advisor"].includes(strategist.mode) &&
              strategist.lines?.length)) &&
            strategist.lines?.length > 0 && (
              <ul className="mt-3 space-y-1.5">
                {strategist.lines.map((l, i) => (
                  <li
                    key={`line-${i}-${(l || "").slice(0, 40)}`}
                    className="text-[13px] text-slate-700 leading-relaxed flex gap-2"
                  >
                    <span className="text-[#002FA7] shrink-0">•</span>
                    <span>{l}</span>
                  </li>
                ))}
              </ul>
            )}
          {showApplyButtons && (
            <div className="mt-4 pt-4 border-t border-slate-100 flex flex-wrap items-center gap-2">
              <Button
                size="sm"
                onClick={onPreviewApply}
                disabled={!canApply}
                data-testid="apply-to-pipeline-btn"
                title={canApply ? "" : "Needs more data (at least 5 leads)"}
                className="h-9 bg-[#002FA7] hover:bg-[#002072] text-[12.5px]"
              >
                <Filter className="h-3.5 w-3.5 mr-1.5" />
                Apply to pipeline
              </Button>
              {!canApply && (
                <span
                  className="text-[11.5px] text-slate-500"
                  data-testid="apply-needs-data"
                >
                  Needs more data (at least 5 leads)
                </span>
              )}
              {lastSnapshot?.available && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={onUndo}
                  data-testid="undo-icp-btn"
                  className="h-9 border-slate-200 text-[12.5px] ml-auto"
                >
                  <Undo2 className="h-3.5 w-3.5 mr-1.5" />
                  Undo last ICP optimization ({lastSnapshot.lead_count})
                </Button>
              )}
            </div>
          )}
        </div>
      )}
      {busy && (
        <div
          className="mt-5 rounded-xl border border-[#B8C9FF] bg-white/70 px-5 py-4 flex items-center gap-3"
          data-testid="strategist-loading"
        >
          <div className="h-4 w-4 rounded-full border-2 border-[#002FA7] border-t-transparent animate-spin" />
          <p className="text-[13px] text-slate-600 italic">Analyzing pipeline…</p>
        </div>
      )}
    </section>
  );
}



// ============ DAILY AI SALES BRIEF (Phase 3 — Prompt 9) ============
// Compact 3-line morning card. Deterministic content from /api/pipeline/brief.

function DailySalesBrief({ brief }) {
  const lines = brief?.brief_lines || [];
  if (!lines.length) return null;
  const insights = brief?.insights || {};
  return (
    <section
      className="relative overflow-hidden rounded-2xl border border-[#B8C9FF] bg-gradient-to-r from-[#EEF4FF] via-[#F6F9FF] to-white px-5 py-4 shadow-[0_1px_2px_rgba(0,47,167,0.04)]"
      data-testid="daily-sales-brief"
    >
      <div className="flex items-start gap-4">
        <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-[#002FA7] to-[#3A5DFF] text-white grid place-items-center shrink-0 shadow-sm">
          <Sunrise className="h-5 w-5" strokeWidth={2.25} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <div className="label-caps !text-[10px] text-[#002FA7]">
              Today's sales brief
            </div>
            {brief?.has_data === false && (
              <span className="text-[10.5px] font-semibold text-amber-700 uppercase tracking-wider">
                · limited data
              </span>
            )}
          </div>
          <ul className="mt-1.5 space-y-0.5" data-testid="brief-lines">
            {lines.map((line, i) => (
              <li
                key={`brief-${i}-${(line || "").slice(0, 40)}`}
                className="text-[13.5px] text-slate-800 leading-relaxed font-medium"
                data-testid={`brief-line-${i}`}
              >
                {line}
              </li>
            ))}
          </ul>
        </div>
        <div className="hidden md:flex items-center gap-1.5 shrink-0">
          {(insights.need_followup_today ?? 0) > 0 && (
            <Link
              to="/leads"
              className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-semibold bg-white text-[#002FA7] border border-[#B8C9FF] hover:bg-[#F0F5FF] transition-colors"
              data-testid="brief-cta-followup"
            >
              <Send className="h-3 w-3" />
              Follow up
            </Link>
          )}
          {(insights.high_intent ?? 0) > 0 && (
            <Link
              to="/leads"
              className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-semibold bg-emerald-50 text-emerald-800 border border-emerald-200 hover:bg-emerald-100 transition-colors"
              data-testid="brief-cta-hot"
            >
              <Flame className="h-3 w-3" />
              Hot leads
            </Link>
          )}
        </div>
      </div>
    </section>
  );
}



// ============ AI SALES INTELLIGENCE PANEL (Phase 5) ============
// Reuses Command Center styling so the section feels native. Reads from
// /api/learning/insights and degrades gracefully when sample size is thin.

function AiSalesIntelligence({ learning, autoStatus }) {
  const hasData = !!learning?.has_enough_data;
  // Memoize the derived row list — avoids re-allocating this array on every
  // parent render. Static shape; only invalidates when the learning blob changes.
  const rows = useMemo(
    () =>
      hasData
        ? [
            { label: "Top insight", value: learning.insight, testid: "learning-insight" },
            { label: "Best tone", value: learning.top_performing_tone, testid: "learning-tone" },
            { label: "Best hook", value: learning.best_hook, testid: "learning-hook" },
            { label: "Best ICP", value: learning.best_icp, testid: "learning-icp" },
            { label: "Best channel", value: learning.best_channel, testid: "learning-channel" },
          ].filter((r) => r.value)
        : [],
    [hasData, learning],
  );
  const nonInsightRows = useMemo(
    () => rows.filter((r) => r.label !== "Top insight"),
    [rows],
  );
  const mode = (autoStatus?.execution_mode || "").toLowerCase();
  const modeCls = {
    autonomous: "bg-emerald-50 text-emerald-800 border-emerald-200",
    assisted: "bg-blue-50 text-[#002FA7] border-blue-200",
    manual: "bg-slate-100 text-slate-600 border-slate-200",
  }[mode] || "bg-slate-100 text-slate-600 border-slate-200";

  return (
    <section
      className="rounded-2xl border border-[#B8C9FF] bg-gradient-to-br from-[#F0F5FF] via-[#F6F9FF] to-white p-6 shadow-[0_1px_2px_rgba(0,47,167,0.04)]"
      data-testid="ai-sales-intelligence"
    >
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-xl bg-[#002FA7] text-white grid place-items-center shrink-0 shadow-sm">
          <Lightbulb className="h-4.5 w-4.5" strokeWidth={2.25} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-display text-[17px] font-bold tracking-tight text-slate-900">
            AI Sales Intelligence
          </div>
          <div className="text-[12px] text-slate-500 leading-relaxed">
            What the system is learning from real outcomes. Updates as you send, reply, and call.
          </div>
        </div>
        {typeof learning?.sample_size === "number" && (
          <span
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10.5px] font-semibold border bg-white text-slate-600 border-slate-200 shrink-0"
            data-testid="learning-sample-size"
            title="Tracked SDR events informing these insights"
          >
            {learning.sample_size} event{learning.sample_size === 1 ? "" : "s"}
          </span>
        )}
        {mode && (
          <span
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10.5px] font-semibold border shrink-0 ${modeCls}`}
            data-testid="auto-mode-chip"
            title="Current execution mode — change in Setup"
          >
            <Zap className="h-3 w-3" />
            Auto: {mode.charAt(0).toUpperCase() + mode.slice(1)}
          </span>
        )}
      </div>

      {!hasData ? (
        <div
          className="mt-4 rounded-xl border border-white/80 bg-white/90 px-5 py-4"
          data-testid="learning-empty-state"
        >
          <p className="text-[13px] text-slate-700 leading-relaxed">
            {learning?.insight ||
              "Collecting data — need more outbound activity to spot patterns."}
          </p>
          <p className="mt-1 text-[12px] text-slate-500">
            {learning?.recommendation ||
              "Run more qualifications and outreach to unlock learning insights."}
          </p>
        </div>
      ) : (
        <div className="mt-4 space-y-3">
          <div
            className="rounded-xl border border-white/80 bg-white/90 px-5 py-4"
            data-testid="learning-headline"
          >
            <div className="label-caps !text-[10px] text-[#002FA7]">
              Top insight
            </div>
            <p className="mt-1 text-[14px] text-slate-900 font-semibold leading-relaxed">
              {learning.insight}
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-2">
            {nonInsightRows.map((r) => (
              <div
                key={r.label}
                className="rounded-md border border-white/80 bg-white/80 px-3 py-2.5"
                data-testid={r.testid}
              >
                <div className="label-caps !text-[9.5px] text-slate-500">
                  {r.label}
                </div>
                <p className="mt-0.5 text-[13px] text-slate-800 font-semibold">
                  {r.value}
                </p>
              </div>
            ))}
          </div>

          {learning.recommendation && (
            <div
              className="rounded-md border border-[#B8C9FF] bg-[#F0F5FF] px-3 py-2.5"
              data-testid="learning-recommendation"
            >
              <div className="label-caps !text-[10px] text-[#002FA7]">
                Recommendation
              </div>
              <p className="mt-1 text-[13px] text-slate-800 leading-relaxed">
                {learning.recommendation}
              </p>
            </div>
          )}
        </div>
      )}
    </section>
  );
}



// ============ SALES DOCTOR PANEL (Tasks 1, 2, 5, 6) ============

const DOCTOR_MODES = [
  { key: "diagnose",  label: "Diagnose",  Icon: Stethoscope, testid: "doctor-diagnose" },
  { key: "prescribe", label: "Prescribe", Icon: Lightbulb,   testid: "doctor-prescribe" },
  { key: "forecast",  label: "Forecast",  Icon: TrendingUp,  testid: "doctor-forecast" },
];

function SalesDoctorPanel({
  doctor, busy, onRun, strategyQuestion, setStrategyQuestion,
}) {
  return (
    <section
      className="rounded-2xl border border-[#B8C9FF] bg-gradient-to-br from-[#F0F5FF] via-[#F6F9FF] to-white p-6 shadow-[0_1px_2px_rgba(0,47,167,0.04)]"
      data-testid="sales-doctor-panel"
    >
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-xl bg-[#002FA7] text-white grid place-items-center shrink-0 shadow-sm">
          <Stethoscope className="h-4.5 w-4.5" strokeWidth={2.25} />
        </div>
        <div>
          <div className="font-display text-[17px] font-bold tracking-tight text-slate-900">
            AI Sales Doctor
          </div>
          <div className="text-[12px] text-slate-500 leading-relaxed">
            Consultant-grade diagnosis, prescriptions, and revenue forecasting.
          </div>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        {DOCTOR_MODES.map(({ key, label, Icon, testid }) => (
          <Button
            key={key}
            onClick={() => onRun(key)}
            disabled={!!busy}
            size="sm"
            variant={doctor?.mode === key ? "default" : "outline"}
            className={`h-9 text-[12.5px] ${doctor?.mode === key ? "bg-[#002FA7] hover:bg-[#002072]" : "border-[#B8C9FF] text-[#002FA7] hover:bg-[#F0F5FF]"}`}
            data-testid={testid}
          >
            {busy === key ? (
              <div className="h-3 w-3 mr-1.5 rounded-full border-2 border-current/30 border-t-current animate-spin" />
            ) : (
              <Icon className="h-3.5 w-3.5 mr-1.5" />
            )}
            {label}
          </Button>
        ))}
      </div>

      <div className="mt-3 flex items-center gap-2">
        <Input
          value={strategyQuestion}
          onChange={(e) => setStrategyQuestion(e.target.value)}
          placeholder='Ask strategically — e.g., "How do I increase sales by 50%?"'
          className="h-9 text-[13px]"
          data-testid="doctor-strategy-input"
          onKeyDown={(e) => {
            if (e.key === "Enter" && !busy) onRun("strategy");
          }}
        />
        <Button
          onClick={() => onRun("strategy")}
          disabled={!!busy || !strategyQuestion.trim()}
          size="sm"
          className="h-9 bg-[#002FA7] hover:bg-[#002072] text-[12.5px] shrink-0"
          data-testid="doctor-strategy-btn"
        >
          {busy === "strategy" ? (
            <div className="h-3 w-3 mr-1.5 rounded-full border-2 border-white/30 border-t-white animate-spin" />
          ) : (
            <Sparkles className="h-3.5 w-3.5 mr-1.5" />
          )}
          Advise
        </Button>
      </div>

      {doctor && !busy && (
        <div
          className="mt-5 rounded-xl border border-white/80 bg-white/90 px-5 py-4 animate-brain-fade-in"
          data-testid="doctor-result"
        >
          <div className="label-caps !text-[10px] text-[#002FA7]">
            {doctor.title}
          </div>
          <SalesDoctorResult mode={doctor.mode} data={doctor.data} />
        </div>
      )}
    </section>
  );
}

function SalesDoctorResult({ mode, data }) {
  if (!data) return null;
  if (mode === "diagnose") {
    return (
      <div className="mt-2 space-y-2">
        <p
          className="text-[14px] font-semibold text-slate-900"
          data-testid="doctor-problem"
        >
          {data.problem || "No problem identified."}
        </p>
        {data.root_cause && (
          <div className="grid gap-2 mt-2">
            <DoctorRow label="Root cause" value={data.root_cause} color="amber" />
            <DoctorRow label="Impact" value={data.impact} color="red" />
            <DoctorRow label="Priority" value={data.priority} color="blue" />
          </div>
        )}
      </div>
    );
  }
  if (mode === "prescribe") {
    const actions = data.actions || [];
    if (!actions.length) {
      return (
        <p className="mt-2 text-[13px] text-slate-600">
          Not enough data to prescribe specific actions yet.
        </p>
      );
    }
    return (
      <ol
        className="mt-2 space-y-2.5"
        data-testid="doctor-actions"
      >
        {actions.map((a, i) => (
          <li
            key={`rx-${i}-${(a.action || "").slice(0, 30)}`}
            className="rounded-md border border-slate-200 bg-white px-3 py-2.5"
          >
            <div className="flex items-start gap-2">
              <span className="h-5 w-5 rounded-full bg-[#002FA7] text-white text-[10px] font-bold grid place-items-center shrink-0 mt-0.5">
                {i + 1}
              </span>
              <div className="flex-1">
                <p className="text-[13.5px] font-semibold text-slate-900">
                  {a.action}
                </p>
                {a.reason && (
                  <p className="text-[12px] text-slate-600 leading-relaxed mt-0.5">
                    {a.reason}
                  </p>
                )}
                <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                  {a.expected_impact && (
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10.5px] font-semibold bg-emerald-50 text-emerald-800 border border-emerald-200">
                      {a.expected_impact}
                    </span>
                  )}
                  {a.effort && (
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10.5px] font-semibold bg-slate-100 text-slate-700 border border-slate-200">
                      {a.effort} effort
                    </span>
                  )}
                </div>
              </div>
            </div>
          </li>
        ))}
      </ol>
    );
  }
  if (mode === "forecast") {
    return (
      <div className="mt-2 space-y-2">
        <div className="flex items-baseline gap-3">
          <span
            className="text-[28px] font-bold font-display text-[#002FA7] tracking-tight"
            data-testid="doctor-forecast-value"
          >
            {data.forecast_30d || "—"}
          </span>
          {data.confidence && (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10.5px] font-semibold bg-slate-100 text-slate-700 border border-slate-200">
              {data.confidence} confidence
            </span>
          )}
        </div>
        {data.growth_opportunity && (
          <DoctorRow
            label="Growth opportunity"
            value={data.growth_opportunity}
            color="emerald"
          />
        )}
        {data.risk && <DoctorRow label="Risk" value={data.risk} color="red" />}
      </div>
    );
  }
  if (mode === "strategy") {
    const steps = data.tactical_steps || [];
    return (
      <div className="mt-2 space-y-2.5" data-testid="doctor-strategy-body">
        {data.strategy && (
          <p className="text-[13.5px] font-semibold text-slate-900 leading-relaxed">
            {data.strategy}
          </p>
        )}
        {steps.length > 0 && (
          <ol className="space-y-1">
            {steps.map((s, i) => (
              <li
                key={`step-${i}-${(s || "").slice(0, 30)}`}
                className="text-[13px] text-slate-700 leading-relaxed flex gap-2"
              >
                <span className="text-[#002FA7] font-semibold shrink-0">
                  {i + 1}.
                </span>
                <span>{s}</span>
              </li>
            ))}
          </ol>
        )}
        <div className="grid sm:grid-cols-2 gap-2 mt-2">
          {data.expected_impact && (
            <DoctorRow label="Expected impact" value={data.expected_impact} color="emerald" />
          )}
          {data.timeline && (
            <DoctorRow label="Timeline" value={data.timeline} color="blue" />
          )}
        </div>
      </div>
    );
  }
  return null;
}

function DoctorRow({ label, value, color }) {
  const cls = {
    emerald: "bg-emerald-50 text-emerald-800 border-emerald-200",
    amber:   "bg-amber-50 text-amber-800 border-amber-200",
    red:     "bg-red-50 text-red-700 border-red-200",
    blue:    "bg-blue-50 text-[#002FA7] border-blue-200",
  }[color] || "bg-slate-100 text-slate-700 border-slate-200";
  return (
    <div className="flex items-start gap-2">
      <span
        className={`inline-flex items-center justify-center h-6 px-2 rounded-md text-[10px] font-bold uppercase tracking-wider border shrink-0 ${cls}`}
      >
        {label}
      </span>
      <p className="text-[13px] text-slate-800 leading-relaxed pt-0.5">{value}</p>
    </div>
  );
}


// ============ TOP ACTIONS TODAY (Task 1) ============
const ACTION_LABEL = {
  call_now: "Call now",
  call_replied: "Call now",
  send_follow_up: "Send follow-up",
  deprioritize: "Deprioritize",
};
const ACTION_CLASS = {
  call_now: "bg-[#002FA7] text-white hover:bg-[#002072]",
  call_replied: "bg-emerald-600 text-white hover:bg-emerald-700",
  send_follow_up: "bg-white text-[#002FA7] border border-[#002FA7] hover:bg-blue-50",
  deprioritize: "bg-white text-slate-600 border border-slate-300 hover:bg-slate-50",
};
const IMPACT_CLASS = {
  High: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Medium: "bg-amber-50 text-amber-700 border-amber-200",
  Low: "bg-slate-100 text-slate-600 border-slate-200",
};

function TopActionsToday({ actions }) {
  const list = actions?.actions || [];
  if (actions === null) {
    return (
      <div
        className="rounded-2xl bg-white border border-slate-200 p-5 h-full"
        data-testid="top-actions-card"
      >
        <div className="label-caps text-[#002FA7] mb-4">Top Actions Today</div>
        <div className="space-y-3" data-testid="top-actions-skeleton">
          {[0, 1, 2].map((i) => (
            <div key={i} className="flex items-center justify-between gap-3 py-2">
              <div className="flex-1 space-y-2">
                <div className="h-4 w-40 rounded bg-slate-100 animate-pulse" />
                <div className="h-3 w-64 rounded bg-slate-100 animate-pulse" />
              </div>
              <div className="h-8 w-20 rounded-full bg-slate-100 animate-pulse" />
            </div>
          ))}
        </div>
      </div>
    );
  }
  return (
    <div
      className="rounded-2xl bg-white border border-slate-200 p-5 h-full"
      data-testid="top-actions-card"
    >
      <div className="flex items-center gap-2 mb-4">
        <Zap className="h-4 w-4 text-[#002FA7]" />
        <div className="label-caps text-[#002FA7]">Top Actions Today</div>
        {list.length > 0 && (
          <span className="ml-auto text-xs text-slate-500">
            {list.length} prioritized
          </span>
        )}
      </div>
      {list.length === 0 ? (
        <div className="text-sm text-slate-500 py-4" data-testid="top-actions-empty">
          No urgent actions right now. Qualify more leads to surface ROI plays.
        </div>
      ) : (
        <div className="space-y-3">
          {list.map((a) => (
            <div
              key={a.lead_id}
              className="flex items-start justify-between gap-3 py-2 border-b border-slate-100 last:border-b-0"
              data-testid={`top-action-row-${a.action}`}
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[15px] font-semibold text-slate-900 truncate max-w-[220px]">
                    {a.lead_name}
                  </span>
                  {a.company && (
                    <span className="text-[13px] text-slate-500 truncate max-w-[180px]">
                      · {a.company}
                    </span>
                  )}
                  <span
                    className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md border ${
                      IMPACT_CLASS[a.expected_impact] || IMPACT_CLASS.Medium
                    }`}
                  >
                    {a.expected_impact} impact
                  </span>
                  <ScoreExplainer score={a.score} breakdown={a.score_breakdown} />
                </div>
                <div className="text-[13px] text-slate-600 mt-1 leading-snug">
                  {a.reason}
                </div>
                <div className="text-[11px] text-slate-400 mt-1">
                  ETA: {a.eta}
                </div>
              </div>
              <Link to={`/leads/${a.lead_id}`} className="shrink-0">
                <Button
                  size="sm"
                  className={`h-8 rounded-full px-3 text-xs font-semibold ${
                    ACTION_CLASS[a.action] || ACTION_CLASS.send_follow_up
                  }`}
                  data-testid={`top-action-btn-${a.action}`}
                >
                  {ACTION_LABEL[a.action] || "Open"}
                </Button>
              </Link>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============ AI IMPACT PANEL (Task 2) ============

function AiImpactPanel({ impact }) {
  if (!impact) {
    return (
      <div
        className="rounded-2xl bg-white border border-slate-200 p-5 h-full"
        data-testid="ai-impact-card"
      >
        <div className="label-caps text-[#002FA7] mb-3">AI Impact (7d)</div>
        <div className="grid grid-cols-2 gap-3" data-testid="ai-impact-skeleton">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="space-y-2">
              <div className="h-3 w-20 rounded bg-slate-100 animate-pulse" />
              <div className="h-7 w-16 rounded bg-slate-100 animate-pulse" />
            </div>
          ))}
        </div>
        <div className="mt-4 h-10 rounded-lg bg-slate-100 animate-pulse" />
      </div>
    );
  }
  const deltaPositive = (impact.reply_rate_delta || "").trim().startsWith("+");
  return (
    <div
      className="rounded-2xl bg-white border border-slate-200 p-5 h-full"
      data-testid="ai-impact-card"
    >
      <div className="flex items-center gap-2 mb-4">
        <TrendingUp className="h-4 w-4 text-[#002FA7]" />
        <div className="label-caps text-[#002FA7]">AI Impact · last {impact.period_days}d</div>
      </div>
      <div className="grid grid-cols-2 gap-3 mb-4">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-slate-500">
            Reply rate
          </div>
          <div className="flex items-baseline gap-2 mt-1">
            <span className="font-display text-2xl font-extrabold text-slate-900 leading-none">
              {impact.reply_rate}
            </span>
            <span
              className={`text-[12px] font-semibold ${
                deltaPositive ? "text-emerald-600" : "text-red-600"
              }`}
              data-testid="ai-impact-delta"
            >
              {impact.reply_rate_delta}
            </span>
          </div>
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-wider text-slate-500">
            Meetings
          </div>
          <div className="font-display text-2xl font-extrabold text-slate-900 mt-1 leading-none">
            {impact.meetings}
          </div>
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-wider text-slate-500">
            Meeting rate
          </div>
          <div className="font-display text-2xl font-extrabold text-slate-900 mt-1 leading-none">
            {impact.meeting_rate}
          </div>
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-wider text-slate-500">
            Revenue influenced
          </div>
          <div className="font-display text-xl font-extrabold text-[#002FA7] mt-1 leading-none truncate">
            {impact.revenue_influenced}
          </div>
        </div>
      </div>
      <div
        className="text-[12px] text-slate-600 bg-blue-50/60 border border-blue-100 rounded-lg px-3 py-2 leading-snug"
        data-testid="ai-impact-insight"
      >
        {impact.insight}
      </div>
    </div>
  );
}


// ============ SCORE EXPLAINER (Task 1 — micro-explainer) ============
// Hover OR click the small "i" next to each action row to see exactly which
// factors contributed to the priority score. Zero layout change — popover-only.
function ScoreExplainer({ score, breakdown }) {
  const entries = Object.entries(breakdown || {});
  if (!entries.length) return null;
  const top = entries[0];  // highest weighted factor, used in the summary line
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center gap-1 text-[10px] font-semibold text-[#002FA7] bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-full px-2 h-5 transition-colors focus:outline-none focus:ring-2 focus:ring-[#002FA7]/30"
          aria-label="Why this action?"
          data-testid="score-explainer-trigger"
        >
          Score {score}
          <Info className="h-2.5 w-2.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-64 p-3"
        data-testid="score-explainer-popover"
      >
        <div className="label-caps text-[#002FA7] text-[10px] mb-2">
          Score breakdown
        </div>
        <ul className="space-y-1.5">
          {entries.map(([label, points]) => (
            <li
              key={label}
              className="flex items-center justify-between text-[12px]"
            >
              <span className="text-slate-700">{label}</span>
              <span className="font-mono font-bold text-emerald-700">
                +{points}
              </span>
            </li>
          ))}
          <li className="flex items-center justify-between text-[12px] border-t border-slate-100 pt-1.5 mt-1.5">
            <span className="font-semibold text-slate-900">Total</span>
            <span className="font-mono font-bold text-[#002FA7]">{score}</span>
          </li>
        </ul>
        <p className="mt-3 text-[11px] text-slate-500 leading-snug italic">
          {top[1] >= 50
            ? "This is your highest ROI action right now."
            : `Top driver: ${top[0]}. Act while the signal is fresh.`}
        </p>
      </PopoverContent>
    </Popover>
  );
}


// ============ LEAD SOURCE ROI CARD (Task 1) ============
// Pure-aggregation per-source conversion panel. No LLM, no extra DB hits
// beyond the initial `/api/lead-source-roi` call.
const LEAD_SOURCE_LABEL = {
  apollo: "Apollo",
  zoominfo: "ZoomInfo",
  linkedin: "LinkedIn",
  csv: "CSV",
  manual: "Manual",
};

// ============ BOOKING FUNNEL CARD ============
// Closed-loop attribution: outreach → click → booking. Sourced from
// /api/analytics/booking-funnel which aggregates sdr_events.

const FUNNEL_TONE = {
  green: { bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-200", dot: "bg-emerald-500" },
  yellow: { bg: "bg-amber-50", text: "text-amber-800", border: "border-amber-200", dot: "bg-amber-500" },
  red: { bg: "bg-red-50", text: "text-red-700", border: "border-red-200", dot: "bg-red-500" },
};

function _funnelTone(rate, kind) {
  // Click rate benchmarks: >=30% green, >=10% yellow, else red.
  // Booking rate benchmarks: >=25% green, >=10% yellow, else red.
  const thresholds = kind === "click"
    ? { green: 30, yellow: 10 }
    : { green: 25, yellow: 10 };
  if (rate >= thresholds.green) return FUNNEL_TONE.green;
  if (rate >= thresholds.yellow) return FUNNEL_TONE.yellow;
  return FUNNEL_TONE.red;
}

function FunnelMetric({ testId, label, value, accent }) {
  return (
    <div
      className="rounded-xl bg-white border border-slate-200 p-4 flex flex-col"
      data-testid={testId}
    >
      <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
        {label}
      </div>
      <div
        className="mt-1 text-3xl font-extrabold tracking-tight"
        style={{ color: accent || "#0F172A" }}
      >
        {value}
      </div>
    </div>
  );
}

function FunnelRatePill({ testId, label, rate, kind }) {
  const tone = _funnelTone(rate, kind);
  return (
    <div
      className={`rounded-xl border ${tone.border} ${tone.bg} px-4 py-3 flex items-center justify-between gap-3`}
      data-testid={testId}
      data-tone={tone === FUNNEL_TONE.green ? "green" : tone === FUNNEL_TONE.yellow ? "yellow" : "red"}
    >
      <div className="flex items-center gap-2">
        <span className={`h-2 w-2 rounded-full ${tone.dot}`} />
        <span className="text-[12.5px] font-semibold text-slate-700">
          {label}
        </span>
      </div>
      <div className={`text-[14px] font-extrabold ${tone.text}`}>
        {rate.toFixed(1)}%
      </div>
    </div>
  );
}

// Tiny inline sparkline — pure SVG, 7 data points.
function Sparkline({ values, color, testId }) {
  if (!values || values.length === 0) {
    return null;
  }
  const W = 120;
  const H = 28;
  const max = Math.max(1, ...values);
  const stepX = values.length > 1 ? W / (values.length - 1) : 0;
  const points = values
    .map((v, i) => {
      const x = i * stepX;
      const y = H - (v / max) * H;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");
  const lastX = (values.length - 1) * stepX;
  const lastY = H - (values[values.length - 1] / max) * H;
  return (
    <svg
      width={W}
      height={H}
      viewBox={`0 0 ${W} ${H}`}
      data-testid={testId}
      className="overflow-visible"
    >
      <polyline
        fill="none"
        stroke={color}
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        points={points}
      />
      <circle cx={lastX} cy={lastY} r="2.5" fill={color} />
    </svg>
  );
}

// ============ HorizontalFunnelFlow — premium centerpiece visualisation ============
// 3 stages joined by gradient arrows + animated flow line. Designed to be the
// dashboard's hero: a CEO can read it in 3 seconds.
function FunnelStage({ icon: Icon, label, value, gradient, glow, testId }) {
  const display = useAnimatedCount(value, { durationMs: 900 });
  return (
    <div
      data-testid={testId}
      className="relative flex-1 min-w-[140px] rounded-2xl bg-white border border-slate-200 px-5 py-5 sm:px-6 sm:py-6 text-center shadow-[0_10px_30px_-15px_rgba(15,23,42,0.18)]"
    >
      {/* Top accent bar */}
      <div
        aria-hidden="true"
        className={`absolute inset-x-5 top-0 h-1 rounded-b-full bg-gradient-to-r ${gradient}`}
      />
      <div
        aria-hidden="true"
        className={`absolute -inset-1 rounded-3xl ${glow} blur-2xl opacity-30 -z-10`}
      />
      <div
        className={`mx-auto mb-3 inline-flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br ${gradient} text-white shadow-md`}
      >
        <Icon className="h-5 w-5" />
      </div>
      <div className="text-[10.5px] font-bold uppercase tracking-[0.18em] text-slate-500">
        {label}
      </div>
      <div className="mt-1.5 font-display text-4xl sm:text-5xl font-extrabold tracking-tight text-slate-900 tabular-nums">
        {display}
      </div>
    </div>
  );
}

function FunnelArrow({ rate, kind, testId }) {
  const tone = _funnelTone(rate, kind);
  const ratePct = `${rate.toFixed(1)}%`;
  const isHealthy = tone === "good";
  const isOk = tone === "ok";
  const colorRing = isHealthy
    ? "from-emerald-500 to-emerald-400"
    : isOk
    ? "from-amber-500 to-amber-400"
    : "from-slate-400 to-slate-300";
  const pillColor = isHealthy
    ? "bg-emerald-50 border-emerald-200 text-emerald-700"
    : isOk
    ? "bg-amber-50 border-amber-200 text-amber-700"
    : "bg-slate-50 border-slate-200 text-slate-600";
  return (
    <div
      data-testid={testId}
      data-rate={rate}
      data-tone={tone}
      className="relative flex flex-col items-center justify-center sm:px-2 py-2 min-w-[90px]"
    >
      {/* Animated horizontal flow line — visible on sm and up */}
      <div className="hidden sm:flex absolute inset-x-0 top-1/2 -translate-y-1/2 h-[2px] overflow-hidden">
        <div
          className={`h-full w-full bg-gradient-to-r ${colorRing} opacity-40`}
        />
      </div>
      <div
        className={`relative inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-bold tabular-nums shadow-sm ${pillColor}`}
      >
        <span>{ratePct}</span>
        <ArrowRight className="h-3 w-3" />
      </div>
      {/* Mobile fallback — vertical chevron */}
      <div className="sm:hidden mt-1 text-slate-300">
        <ArrowRight className="h-4 w-4 rotate-90" />
      </div>
    </div>
  );
}

function HorizontalFunnelFlow({ emails, clicks, meetings, clickRate, bookingRate }) {
  return (
    <div
      data-testid="horizontal-funnel-flow"
      className="relative flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-2"
    >
      <FunnelStage
        testId="funnel-stage-emails"
        icon={Send}
        label="Emails Sent"
        value={emails}
        gradient="from-indigo-500 to-blue-500"
        glow="bg-indigo-300"
      />
      <FunnelArrow rate={clickRate} kind="click" testId="funnel-arrow-click" />
      <FunnelStage
        testId="funnel-stage-clicks"
        icon={Activity}
        label="Link Clicks"
        value={clicks}
        gradient="from-orange-500 to-rose-500"
        glow="bg-orange-300"
      />
      <FunnelArrow rate={bookingRate} kind="booking" testId="funnel-arrow-booking" />
      <FunnelStage
        testId="funnel-stage-meetings"
        icon={CalendarCheck}
        label="Meetings Booked"
        value={meetings}
        gradient="from-emerald-500 to-teal-500"
        glow="bg-emerald-300"
      />
    </div>
  );
}

function BookingFunnelCard({ funnel, loading }) {
  if (loading && !funnel) {
    return (
      <section
        className="rounded-3xl border border-slate-200 bg-white p-7"
        data-testid="booking-funnel-skeleton"
      >
        <div className="h-5 w-44 bg-slate-100 animate-pulse rounded mb-6" />
        <div className="h-32 bg-slate-50 animate-pulse rounded-2xl" />
      </section>
    );
  }
  const f = funnel || {};
  const emails = Number(f.emails_sent_today || 0);
  const clicks = Number(f.link_clicks_today || 0);
  const meetings = Number(f.meetings_booked_today || 0);
  const clickRate = Number(f.conversion_rates?.click_rate || 0);
  const bookingRate = Number(f.conversion_rates?.booking_rate || 0);
  const trend = f.trend || [];
  const isEmpty = !!f.is_empty;
  const windowLabel = f.window_label || "Live · last 24h";

  return (
    <section
      className="relative overflow-hidden rounded-3xl border border-slate-200 bg-white p-6 sm:p-8 shadow-[0_24px_60px_-30px_rgba(15,23,42,0.25)]"
      data-testid="booking-funnel-card"
      data-is-empty={isEmpty ? "true" : "false"}
    >
      {/* Ambient gradient — barely there, gives the card depth */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 -top-20 h-56 bg-gradient-to-b from-orange-50/60 via-amber-50/30 to-transparent blur-2xl"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -bottom-20 -right-20 h-56 w-56 rounded-full bg-emerald-100/40 blur-3xl"
      />
      <header className="relative flex items-center justify-between gap-3 flex-wrap mb-6">
        <div className="flex items-center gap-3">
          <span className="inline-flex h-10 w-10 rounded-xl bg-gradient-to-br from-orange-500 to-rose-500 items-center justify-center shadow-md shadow-orange-500/30">
            <Flame className="h-5 w-5 text-white" />
          </span>
          <div>
            <div className="text-[10.5px] font-bold uppercase tracking-[0.18em] text-slate-500">
              Today's revenue engine
            </div>
            <div className="text-xl sm:text-2xl font-extrabold tracking-tight text-slate-900 leading-tight">
              Booking Funnel
            </div>
          </div>
        </div>
        <span
          className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 text-[11px] font-bold uppercase tracking-wider border border-emerald-200"
          data-testid="booking-funnel-window"
        >
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
          {windowLabel}
        </span>
      </header>

      {isEmpty ? (
        <div
          className="rounded-xl border border-dashed border-slate-300 bg-white/60 px-4 py-8 text-center"
          data-testid="booking-funnel-empty"
        >
          <div className="text-[13.5px] font-semibold text-slate-800">
            {f.empty_state?.title || "No outreach yet today"}
          </div>
          <div className="mt-1 text-[12px] text-slate-500 max-w-md mx-auto">
            {f.empty_state?.message ||
              "Funnel updates the moment your AI sends an email or a lead clicks a booking link."}
          </div>
          {f.empty_state?.cta_path && (
            <Link
              to={f.empty_state.cta_path}
              data-testid="booking-funnel-empty-cta"
              className="mt-4 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12px] font-semibold bg-slate-900 hover:bg-slate-800 text-white"
            >
              {f.empty_state.cta_label || "Start outreach"} →
            </Link>
          )}
        </div>
      ) : (
        <>
          {/* Horizontal funnel flow — the centrepiece visualization.
              Three stages connected by animated gradient arrows that show
              the conversion math at a glance. */}
          <HorizontalFunnelFlow
            emails={emails}
            clicks={clicks}
            meetings={meetings}
            clickRate={clickRate}
            bookingRate={bookingRate}
          />

          <div
            className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3 sr-only"
            data-testid="booking-funnel-metrics"
          >
            <FunnelMetric
              testId="funnel-emails-sent"
              label="Emails Sent"
              value={emails}
              accent="#002FA7"
            />
            <FunnelMetric
              testId="funnel-link-clicks"
              label="Link Clicks"
              value={clicks}
              accent="#EA580C"
            />
            <FunnelMetric
              testId="funnel-meetings-booked"
              label="Meetings Booked"
              value={meetings}
              accent="#16A34A"
            />
          </div>

          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3 sr-only">
            <FunnelRatePill
              testId="funnel-click-rate"
              label="Click Rate"
              rate={clickRate}
              kind="click"
            />
            <FunnelRatePill
              testId="funnel-booking-rate"
              label="Booking Rate"
              rate={bookingRate}
              kind="booking"
            />
          </div>

          {trend.length > 0 && (
            <div
              className="mt-5 rounded-xl border border-slate-200 bg-white px-4 py-3"
              data-testid="booking-funnel-trend"
            >
              <div className="flex items-center justify-between mb-2">
                <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
                  Last 7 days
                </div>
                <div className="text-[10.5px] text-slate-400 font-mono">
                  emails · clicks · bookings
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="flex items-center gap-2">
                  <Sparkline
                    testId="funnel-sparkline-emails"
                    values={trend.map((d) => d.emails_sent || 0)}
                    color="#002FA7"
                  />
                  <span className="text-[11.5px] text-slate-500 font-mono">
                    {trend.reduce((a, d) => a + (d.emails_sent || 0), 0)}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Sparkline
                    testId="funnel-sparkline-clicks"
                    values={trend.map((d) => d.link_clicks || 0)}
                    color="#EA580C"
                  />
                  <span className="text-[11.5px] text-slate-500 font-mono">
                    {trend.reduce((a, d) => a + (d.link_clicks || 0), 0)}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Sparkline
                    testId="funnel-sparkline-bookings"
                    values={trend.map((d) => d.meetings_booked || 0)}
                    color="#16A34A"
                  />
                  <span className="text-[11.5px] text-slate-500 font-mono">
                    {trend.reduce((a, d) => a + (d.meetings_booked || 0), 0)}
                  </span>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </section>
  );
}

// ============ AI OPTIMIZATION INSIGHTS (Phase 3 / Task 1) ============

const IMPACT_TONE = {
  high: { bg: "bg-rose-50", border: "border-rose-200", text: "text-rose-700", dot: "bg-rose-500" },
  medium: { bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-800", dot: "bg-amber-500" },
  low: { bg: "bg-slate-50", border: "border-slate-200", text: "text-slate-600", dot: "bg-slate-400" },
};

function AiInsightsCard({ insights, loading }) {
  if (loading && !insights) {
    return (
      <section
        className="rounded-2xl border border-slate-200 bg-white p-5 h-full"
        data-testid="ai-insights-skeleton"
      >
        <div className="h-5 w-44 bg-slate-100 animate-pulse rounded mb-4" />
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-16 bg-slate-50 animate-pulse rounded mb-2" />
        ))}
      </section>
    );
  }
  const list = insights?.insights || [];
  const isEmpty = !!insights?.is_empty || list.length === 0;
  const windowLabel = insights?.window_label || "Based on last 7 days activity";
  return (
    <section
      className="rounded-2xl border border-indigo-100 bg-gradient-to-br from-indigo-50/50 via-white to-violet-50/30 p-5 sm:p-6 h-full"
      data-testid="ai-insights-card"
      data-is-empty={isEmpty ? "true" : "false"}
    >
      <header className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <div className="flex items-center gap-2">
          <span className="inline-flex h-7 w-7 rounded-md bg-indigo-500/10 border border-indigo-200 items-center justify-center">
            <Sparkles className="h-4 w-4 text-indigo-600" />
          </span>
          <div>
            <div className="text-[15px] font-extrabold tracking-tight text-slate-900">
              AI Optimization Insights
            </div>
            <div className="text-[11.5px] text-slate-500">
              {windowLabel}
            </div>
          </div>
        </div>
      </header>
      {isEmpty ? (
        <div
          className="rounded-xl border border-dashed border-slate-300 bg-white/60 px-4 py-8 text-center"
          data-testid="ai-insights-empty"
        >
          <div className="text-[13.5px] font-semibold text-slate-800">
            {insights?.empty_state?.title || "Insights appear once your AI starts running"}
          </div>
          <div className="mt-1 text-[12px] text-slate-500 max-w-md mx-auto">
            {insights?.empty_state?.message ||
              "Send your first batch of outreach — the engine will surface what's working and what to fix the moment data flows in."}
          </div>
          {insights?.empty_state?.cta_path && (
            <Link
              to={insights.empty_state.cta_path}
              data-testid="ai-insights-empty-cta"
              className="mt-4 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12px] font-semibold bg-slate-900 hover:bg-slate-800 text-white"
            >
              {insights.empty_state.cta_label || "Start outreach"} →
            </Link>
          )}
        </div>
      ) : (
        <div className="space-y-2.5" data-testid="ai-insights-list">
          {list.map((it, idx) => {
            const tone = IMPACT_TONE[it.impact] || IMPACT_TONE.low;
            return (
              <div
                key={`${it.impact}-${(it.message || "").slice(0, 40)}`}
                className={`rounded-xl border ${tone.border} bg-white px-4 py-3 flex items-start gap-3`}
                data-testid={`ai-insight-${idx}`}
                data-impact={it.impact}
              >
                <span className={`mt-1.5 h-2 w-2 rounded-full ${tone.dot} shrink-0`} />
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-semibold text-slate-900 leading-snug">
                    {it.message}
                  </div>
                  <div className="mt-0.5 text-[12px] text-slate-600 leading-snug">
                    {it.recommendation}
                  </div>
                </div>
                <span
                  className={`shrink-0 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border ${tone.border} ${tone.bg} ${tone.text}`}
                >
                  {it.impact || "low"}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

// ============ TOP MESSAGES (Phase 3 / Task 3) ============

function TopMessagesCard({ ranking, loading }) {
  const [optimizing, setOptimizing] = useState(false);
  const [variants, setVariants] = useState(null); // { variants:[3], source }
  const [optimizedFor, setOptimizedFor] = useState("");
  const [copiedIdx, setCopiedIdx] = useState(null);

  if (loading && !ranking) {
    return (
      <section
        className="rounded-2xl border border-slate-200 bg-white p-5 h-full"
        data-testid="top-messages-skeleton"
      >
        <div className="h-5 w-44 bg-slate-100 animate-pulse rounded mb-4" />
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-12 bg-slate-50 animate-pulse rounded mb-2" />
        ))}
      </section>
    );
  }
  const messages = ranking?.messages || [];
  const isEmpty = !!ranking?.is_empty || messages.length === 0;
  const windowLabel = ranking?.window_label || "Sorted by booking rate";
  const topSubject = messages[0]?.subject || "";
  const canOptimize = topSubject.trim().length > 0;

  const onGenerate = async () => {
    if (!canOptimize) return;
    setOptimizing(true);
    setVariants(null);
    setCopiedIdx(null);
    try {
      const data = await generateSubjectVariants(topSubject);
      setVariants(data);
      setOptimizedFor(topSubject);
    } catch (e) {
      toast.error("Couldn't generate variants", {
        description: e?.response?.data?.detail || "Try again in a moment.",
      });
    } finally {
      setOptimizing(false);
    }
  };

  const onCopy = async (idx, text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedIdx(idx);
      toast.success("Copied to clipboard");
      setTimeout(() => setCopiedIdx((c) => (c === idx ? null : c)), 1800);
    } catch {
      toast.error("Copy failed");
    }
  };

  return (
    <section
      className="rounded-2xl border border-amber-100 bg-gradient-to-br from-amber-50/50 via-white to-orange-50/30 p-5 sm:p-6 h-full"
      data-testid="top-messages-card"
      data-is-empty={isEmpty ? "true" : "false"}
    >
      <header className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <div className="flex items-center gap-2">
          <span className="inline-flex h-7 w-7 rounded-md bg-amber-500/10 border border-amber-200 items-center justify-center">
            <Trophy className="h-4 w-4 text-amber-600" />
          </span>
          <div>
            <div className="text-[15px] font-extrabold tracking-tight text-slate-900">
              Top Performing Messages
            </div>
            <div className="text-[11.5px] text-slate-500">
              {windowLabel}
            </div>
          </div>
        </div>
      </header>
      {isEmpty ? (
        <div
          className="rounded-xl border border-dashed border-slate-300 bg-white/60 px-4 py-8 text-center"
          data-testid="top-messages-empty"
        >
          <div className="text-[13.5px] font-semibold text-slate-800">
            {ranking?.empty_state?.title || "No messages tracked yet"}
          </div>
          <div className="mt-1 text-[12px] text-slate-500 max-w-md mx-auto">
            {ranking?.empty_state?.message ||
              "Send your first cold emails — top performers appear here as soon as recipients click your booking link."}
          </div>
          {ranking?.empty_state?.cta_path && (
            <Link
              to={ranking.empty_state.cta_path}
              data-testid="top-messages-empty-cta"
              className="mt-4 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12px] font-semibold bg-slate-900 hover:bg-slate-800 text-white"
            >
              {ranking.empty_state.cta_label || "Open Leads"} →
            </Link>
          )}
        </div>
      ) : (
        <ol className="space-y-2.5" data-testid="top-messages-list">
          {messages.slice(0, 5).map((m, idx) => (
          <li
            key={m.body_hash || idx}
            className="rounded-xl border border-slate-200 bg-white px-3.5 py-2.5"
            data-testid={`top-message-${idx}`}
            data-booking-rate={m.booking_rate}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5 text-[10.5px] font-bold uppercase tracking-wider text-amber-700">
                  <span>#{idx + 1}</span>
                  <span className="text-slate-300">·</span>
                  <span className="font-mono text-slate-500 normal-case">
                    {m.sent} sent · {m.clicks} clicks · {m.bookings} booked
                  </span>
                </div>
                <div
                  className="mt-1 text-[13px] font-semibold text-slate-900 leading-snug truncate"
                  title={m.subject}
                >
                  "{m.subject}"
                </div>
              </div>
              <div className="shrink-0 text-right">
                <div className="text-[15px] font-extrabold text-emerald-700 leading-none">
                  {m.booking_rate}%
                </div>
                <div className="text-[10px] text-slate-500 uppercase tracking-wider mt-0.5">
                  booking
                </div>
              </div>
            </div>
          </li>
        ))}
      </ol>
      )}

      {/* AI Subject Optimizer — generate 3 variants from #1 subject */}
      {canOptimize && (
        <div className="mt-4 pt-4 border-t border-amber-100" data-testid="subject-optimizer">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="text-[11px] font-bold uppercase tracking-wider text-slate-600 inline-flex items-center gap-1.5">
              <Wand2 className="h-3.5 w-3.5 text-indigo-600" />
              AI Subject Optimizer
            </div>
            <button
              type="button"
              onClick={onGenerate}
              disabled={optimizing}
              data-testid="generate-variations-btn"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11.5px] font-semibold bg-indigo-600 hover:bg-indigo-700 text-white disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
            >
              {optimizing ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 animate-spin" /> Generating…
                </>
              ) : (
                <>
                  <Sparkles className="h-3.5 w-3.5" /> Generate Variations
                </>
              )}
            </button>
          </div>
          {variants?.variants?.length > 0 && (
            <div
              className="mt-3 space-y-2"
              data-testid="subject-variants-list"
              data-source={variants.source || "llm"}
            >
              <div className="text-[11px] text-slate-500">
                Based on:{" "}
                <span className="font-mono text-slate-700">
                  "{(optimizedFor || topSubject).slice(0, 60)}
                  {(optimizedFor || topSubject).length > 60 ? "…" : ""}"
                </span>
              </div>
              {variants.variants.map((v, idx) => (
                <div
                  key={`${idx}-${v.slice(0, 40)}`}
                  className="group flex items-center justify-between gap-2 rounded-md border border-indigo-100 bg-indigo-50/40 px-3 py-2"
                  data-testid={`subject-variant-${idx}`}
                >
                  <div className="min-w-0 flex-1 text-[12.5px] font-medium text-slate-800 truncate">
                    {v}
                  </div>
                  <button
                    type="button"
                    onClick={() => onCopy(idx, v)}
                    data-testid={`subject-variant-copy-${idx}`}
                    className="shrink-0 inline-flex items-center gap-1 text-[11px] font-semibold text-indigo-700 hover:text-indigo-900 px-2 py-0.5 rounded hover:bg-indigo-100"
                  >
                    {copiedIdx === idx ? (
                      <>
                        <CheckCircle2 className="h-3 w-3" /> Copied
                      </>
                    ) : (
                      <>
                        <Copy className="h-3 w-3" /> Copy
                      </>
                    )}
                  </button>
                </div>
              ))}
              {variants.source === "fallback" && (
                <div className="text-[10.5px] text-slate-500 italic">
                  AI unavailable — showing safe rewrites.
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </section>
  );
}


function LeadSourceRoiCard({ roi, loading }) {
  if (loading && !roi) {
    return (
      <section
        className="rounded-2xl border border-slate-200 bg-white p-5"
        data-testid="lead-source-roi-skeleton"
      >
        <div className="h-5 w-40 bg-slate-100 animate-pulse rounded mb-4" />
        <div className="space-y-2">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-8 bg-slate-50 animate-pulse rounded" />
          ))}
        </div>
      </section>
    );
  }
  const rows = roi?.rows || [];
  const hasData = !!roi?.has_data;

  return (
    <section
      className="rounded-2xl border border-slate-200 bg-white p-5"
      data-testid="lead-source-roi-card"
    >
      <div className="flex items-center gap-2">
        <div className="h-9 w-9 rounded-md bg-[#002FA7]/10 flex items-center justify-center">
          <DollarSign className="h-4 w-4 text-[#002FA7]" />
        </div>
        <div>
          <div className="label-caps text-[#002FA7]">Lead Source ROI</div>
          <div className="text-[12px] text-slate-600">
            Conversion by acquisition channel
          </div>
        </div>
      </div>

      {!hasData || rows.length === 0 ? (
        <div
          className="mt-4 rounded-md border border-dashed border-slate-200 bg-slate-50/60 p-6 text-center text-[13px] text-slate-500"
          data-testid="lead-source-roi-empty"
        >
          Start outreach to see which lead sources perform best.
        </div>
      ) : (
        <div className="mt-4 overflow-x-auto" data-testid="lead-source-roi-table">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="text-left text-[11px] text-slate-500 label-caps border-b border-slate-100">
                <th className="py-2 pr-3 font-semibold">Source</th>
                <th className="py-2 px-3 font-semibold text-right">Leads</th>
                <th className="py-2 px-3 font-semibold text-right">Emails</th>
                <th className="py-2 px-3 font-semibold text-right">Reply %</th>
                <th className="py-2 pl-3 font-semibold text-right">Meeting %</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const isBest = roi?.best_source === r.source;
                const isWorst = roi?.worst_source === r.source;
                const rowClass = isBest
                  ? "bg-emerald-50/60"
                  : isWorst
                  ? "bg-slate-50/80 text-slate-500"
                  : "";
                return (
                  <tr
                    key={r.source}
                    className={`border-b border-slate-50 last:border-0 ${rowClass}`}
                    data-testid={`source-row-${r.source}`}
                    data-rank={isBest ? "best" : isWorst ? "worst" : "mid"}
                  >
                    <td className="py-2.5 pr-3">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-slate-900 capitalize">
                          {LEAD_SOURCE_LABEL[r.source] || r.source}
                        </span>
                        {isBest && (
                          <span
                            className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-700 bg-emerald-100 border border-emerald-200 px-1.5 py-0.5 rounded-full"
                            data-testid={`source-badge-best-${r.source}`}
                          >
                            <TrendingUp className="h-2.5 w-2.5" /> Best
                          </span>
                        )}
                        {isWorst && (
                          <span
                            className="text-[10px] font-semibold text-slate-500 bg-slate-100 border border-slate-200 px-1.5 py-0.5 rounded-full"
                            data-testid={`source-badge-worst-${r.source}`}
                          >
                            Low
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="py-2.5 px-3 text-right font-mono text-slate-800">
                      {r.total_leads}
                    </td>
                    <td className="py-2.5 px-3 text-right font-mono text-slate-800">
                      {r.emails_sent}
                    </td>
                    <td
                      className="py-2.5 px-3 text-right font-mono font-semibold"
                      data-testid={`source-reply-rate-${r.source}`}
                    >
                      {r.reply_rate}%
                    </td>
                    <td
                      className="py-2.5 pl-3 text-right font-mono font-semibold"
                      data-testid={`source-meeting-rate-${r.source}`}
                    >
                      {r.meeting_rate}%
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}


// ============ TRIAL PROGRESS CARD ============
// Only renders for authenticated TRIAL users — demo + paid get nothing.
// Shows 3 usage bars (leads/emails/calls) + a green Upgrade CTA that
// brightens at 70%+ fill. Clicks fire /auth/upgrade to log intent.
function TrialProgressCard() {
  const { user, refresh } = useAuth();
  const [busy, setBusy] = useState(false);

  if (!user || user.plan !== "trial") return null;

  const limits = user.usage_limits || {};
  const counters = user.usage_counters || {};
  const rows = [
    { key: "leads", label: "Leads", used: counters.leads || 0, max: limits.max_leads },
    { key: "emails", label: "Emails", used: counters.emails || 0, max: limits.max_emails },
    { key: "calls", label: "Calls", used: counters.calls || 0, max: limits.max_calls },
  ].filter((r) => r.max != null);

  const highestPct = rows.reduce(
    (acc, r) => Math.max(acc, (r.used / Math.max(r.max, 1)) * 100),
    0,
  );
  const nearLimit = highestPct >= 70;

  const handleUpgrade = async () => {
    setBusy(true);
    try {
      const r = await requestUpgrade("trial_progress_card");
      toast.success("Upgrade request received", {
        description: r.message,
      });
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send request.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      className="rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50 to-white p-5"
      data-testid="trial-progress-card"
    >
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="label-caps text-amber-700 flex items-center gap-2">
            Your trial progress
            {user.activation_reward_granted && (
              <span
                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[9px] font-bold uppercase tracking-wider border border-emerald-200"
                data-testid="trial-extended-badge"
                title={`+${user.activation_reward_emails || 10} email bonus unlocked after activation.`}
              >
                <Sparkles className="h-2.5 w-2.5" />
                Extended
              </span>
            )}
          </div>
          <div className="text-[12px] text-slate-600 mt-1">
            {nearLimit
              ? "You're close to your trial limit — upgrade to keep the momentum."
              : "Test real value now. Upgrade anytime to remove the caps."}
          </div>
        </div>
        <Button
          size="sm"
          onClick={handleUpgrade}
          disabled={busy}
          className={`h-8 rounded-full px-4 text-xs font-semibold transition-all ${
            nearLimit
              ? "bg-[#16A34A] hover:bg-[#15803D] text-white shadow-md shadow-emerald-200 ring-2 ring-emerald-300/40 animate-pulse"
              : "bg-emerald-600 hover:bg-emerald-700 text-white"
          }`}
          data-testid="trial-upgrade-btn"
        >
          Upgrade
        </Button>
      </div>
      <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
        {rows.map((r) => {
          const pct = Math.min(100, Math.round((r.used / Math.max(r.max, 1)) * 100));
          const barColor =
            pct >= 85
              ? "bg-red-500"
              : pct >= 70
              ? "bg-amber-500"
              : "bg-emerald-500";
          const isBoosted =
            r.key === "emails" && user.activation_reward_granted;
          return (
            <div
              key={r.key}
              data-testid={`trial-progress-row-${r.key}`}
              className={isBoosted ? "activation-reward-pulse rounded-md -m-1 p-1" : ""}
            >
              <div className="flex items-center justify-between">
                <span className="text-[12px] font-semibold text-slate-700 flex items-center gap-1.5">
                  {r.label}
                  {isBoosted && (
                    <span
                      className="text-[9px] font-bold text-emerald-600"
                      data-testid="trial-progress-bonus-tag"
                      title={`Includes +${user.activation_reward_emails || 10} activation bonus.`}
                    >
                      +{user.activation_reward_emails || 10}
                    </span>
                  )}
                </span>
                <span
                  className="text-[11px] font-mono text-slate-600"
                  data-testid={`trial-progress-count-${r.key}`}
                >
                  {r.used} / {r.max}
                </span>
              </div>
              <div className="h-2 mt-1 w-full rounded-full bg-slate-100 overflow-hidden">
                <div
                  className={`h-full ${barColor} transition-[width] duration-500`}
                  style={{ width: `${pct}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}


// ============ ACTIVATION CHECKLIST (Workspace Empty State) ============
// Shown ONLY for trial-plan users until all 3 activation steps are done.
// Demo and paid users never see this. Additive — does not alter existing
// Dashboard layout when hidden.
function ActivationChecklist({ leads, stats }) {
  const { user, refresh } = useAuth();
  const claimAttemptedRef = useRef(false);

  const leadsArr = Array.isArray(leads) ? leads : [];
  const step1Done = leadsArr.length > 0;
  const step2Done = leadsArr.some(
    (l) => l?.icp_score != null || (l?.lifecycle && l.lifecycle !== "NEW"),
  );
  const step3Done =
    (stats?.emailed_total ?? stats?.sent ?? 0) > 0 ||
    leadsArr.some((l) => (l?.status || "").toLowerCase() === "sent" || l?.sent_at);

  const allDone = step1Done && step2Done && step3Done;

  // Auto-claim the activation reward the first time a trial user hits 3/3.
  // Guarded by (a) user flag on server, (b) ref to prevent duplicate fires
  // during the same render/mount cycle, (c) 403/400 are swallowed.
  useEffect(() => {
    if (!user || user.plan !== "trial") return;
    if (!allDone) return;
    if (user.activation_reward_granted) return;
    if (claimAttemptedRef.current) return;
    claimAttemptedRef.current = true;
    claimActivationReward()
      .then((r) => {
        if (r?.already_granted) {
          refresh();
          return;
        }
        const bonus = r?.reward_emails || 10;
        toast.success(
          `Nice — you've unlocked extended trial access.`,
          {
            description: `You now have ${bonus} more emails to test.`,
            duration: 7000,
          },
        );
        refresh();
      })
      .catch(() => {
        // silent — server will 400 if checklist isn't truly complete
        // (e.g. race between frontend heuristic + server truth). Not fatal.
      });
  }, [user, allDone, refresh]);

  if (!user || user.plan !== "trial") return null;
  // Once activation is complete, hide — the dashboard takes over.
  if (allDone) return null;

  const firstLeadId = leadsArr[0]?.id;

  const steps = [
    {
      key: "upload",
      title: "Upload your leads",
      desc: "Drop a CSV/XLSX or add a lead manually to kick things off.",
      done: step1Done,
      unlocked: true,
      cta: { label: "Upload leads", to: "/leads", icon: Upload },
    },
    {
      key: "qualify",
      title: "Qualify a lead",
      desc: "Run AI qualification to score ICP fit and surface pain points.",
      done: step2Done,
      unlocked: step1Done,
      cta: {
        label: "Qualify now",
        to: firstLeadId ? `/leads/${firstLeadId}` : "/leads",
        icon: Sparkles,
      },
    },
    {
      key: "send",
      title: "Send your first email",
      desc: "Review the AI-drafted cold email and hit send.",
      done: step3Done,
      unlocked: step1Done && step2Done,
      cta: {
        label: "Open first lead",
        to: firstLeadId ? `/leads/${firstLeadId}` : "/leads",
        icon: Send,
      },
    },
  ];

  const completed = steps.filter((s) => s.done).length;
  const pct = Math.round((completed / steps.length) * 100);

  return (
    <section
      className="rounded-2xl border border-[#002FA7]/15 bg-gradient-to-br from-[#EEF2FF] via-white to-[#F0FDF4] p-6"
      data-testid="activation-checklist"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-full bg-[#002FA7] text-white flex items-center justify-center shrink-0">
            <Rocket className="h-5 w-5" />
          </div>
          <div>
            <div className="label-caps text-[#002FA7]">
              Get started in 3 steps
            </div>
            <h3 className="mt-0.5 font-display text-xl font-extrabold tracking-tight text-slate-900">
              Activate your workspace
            </h3>
            <p className="text-[12.5px] text-slate-600 mt-0.5">
              Finish these to see the full power of Profit10x — your AI Revenue Engine.
            </p>
          </div>
        </div>
        <div
          className="text-right"
          data-testid="activation-progress-summary"
        >
          <div className="text-[11px] font-semibold text-slate-500 label-caps">
            Progress
          </div>
          <div className="font-display text-2xl font-extrabold text-[#002FA7] leading-none">
            {completed}<span className="text-slate-400 text-base">/3</span>
          </div>
          <div className="mt-1 h-1.5 w-28 rounded-full bg-slate-100 overflow-hidden">
            <div
              className="h-full bg-[#002FA7] transition-[width] duration-500"
              style={{ width: `${pct}%` }}
              data-testid="activation-progress-bar"
            />
          </div>
        </div>
      </div>

      <ol className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-3">
        {steps.map((s, idx) => {
          const Icon = s.cta.icon;
          const isActive = !s.done && s.unlocked;
          return (
            <li
              key={s.key}
              data-testid={`activation-step-${s.key}`}
              data-step-status={
                s.done ? "done" : s.unlocked ? "active" : "locked"
              }
              className={`rounded-xl border p-4 transition-all ${
                s.done
                  ? "border-emerald-200 bg-emerald-50/60"
                  : isActive
                  ? "border-[#002FA7]/30 bg-white shadow-sm ring-1 ring-[#002FA7]/10"
                  : "border-slate-200 bg-slate-50/60 opacity-70"
              }`}
            >
              <div className="flex items-center gap-2">
                {s.done ? (
                  <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0" />
                ) : (
                  <Circle
                    className={`h-5 w-5 shrink-0 ${
                      isActive ? "text-[#002FA7]" : "text-slate-400"
                    }`}
                  />
                )}
                <span
                  className={`text-[11px] font-mono ${
                    s.done ? "text-emerald-700" : "text-slate-500"
                  }`}
                >
                  STEP {idx + 1}
                </span>
              </div>
              <div className="mt-2 font-semibold text-[14px] text-slate-900">
                {s.title}
              </div>
              <p className="mt-1 text-[12px] text-slate-600 leading-relaxed">
                {s.desc}
              </p>
              {s.done ? (
                <div
                  className="mt-3 inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700"
                  data-testid={`activation-step-${s.key}-done-chip`}
                >
                  <CheckCircle2 className="h-3.5 w-3.5" /> Completed
                </div>
              ) : s.unlocked ? (
                <Link to={s.cta.to}>
                  <Button
                    size="sm"
                    className="mt-3 h-8 rounded-md bg-[#002FA7] hover:bg-[#002072] text-xs"
                    data-testid={`activation-step-${s.key}-cta`}
                  >
                    <Icon className="h-3.5 w-3.5 mr-1.5" />
                    {s.cta.label}
                  </Button>
                </Link>
              ) : (
                <div
                  className="mt-3 inline-flex items-center gap-1 text-[11px] text-slate-400"
                  data-testid={`activation-step-${s.key}-locked`}
                >
                  <Circle className="h-3 w-3" /> Finish previous step first
                </div>
              )}
            </li>
          );
        })}
      </ol>
    </section>
  );
}

// =====================================================================
// AI SALES COMMAND CENTER components (additive UX layer)
// =====================================================================

// Priority 1: PRIMARY ACTION BANNER — dominant, gradient + pulse glow.
// Shows only when there are leads in the workspace; headline adapts to high-
// intent count. Clicking the CTA takes the user to the Leads page where
// Burst Mode is live. "AI Suggested" tag included (Priority 15).
function PrimaryActionBanner({
  count,
  leadsReady,
  execMode,
  toggleAutonomous,
  execSaving,
}) {
  if (!leadsReady) return null;
  const isAuto = execMode === "autonomous";
  const hasHotLeads = count > 0;
  const headline = hasHotLeads
    ? `${count} High-Intent ${count === 1 ? "Lead" : "Leads"} Ready`
    : "AI Sales Engine is Armed";
  const sub = hasHotLeads
    ? "These leads are most likely to convert today."
    : "Upload or qualify leads — AI will surface who to engage first.";
  return (
    <section
      data-testid="primary-action-banner"
      data-high-intent-count={count}
      className="relative overflow-hidden rounded-2xl border border-indigo-200 bg-gradient-to-r from-[#002FA7] via-indigo-700 to-fuchsia-700 text-white shadow-[0_20px_60px_-20px_rgba(0,47,167,0.55)]"
    >
      <div
        aria-hidden
        className="absolute -top-24 -left-24 h-64 w-64 rounded-full bg-white/10 blur-3xl animate-pulse"
      />
      <div
        aria-hidden
        className="absolute -bottom-24 -right-24 h-72 w-72 rounded-full bg-fuchsia-400/20 blur-3xl animate-pulse"
        style={{ animationDelay: "1.2s" }}
      />
      <div className="relative px-6 md:px-10 py-7 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span
              className="inline-flex items-center gap-1 rounded-full bg-white/15 px-2.5 py-0.5 text-[11px] font-semibold tracking-wide uppercase backdrop-blur-sm"
              data-testid="banner-ai-tag"
            >
              <Sparkles className="h-3 w-3" /> AI Suggested
            </span>
            {hasHotLeads && (
              <span className="inline-flex items-center gap-1 rounded-full bg-orange-500/30 px-2.5 py-0.5 text-[11px] font-semibold text-orange-50">
                <Flame className="h-3 w-3" /> Best Time Now
              </span>
            )}
          </div>
          <h2
            className="mt-3 font-display font-extrabold tracking-tight text-3xl sm:text-4xl lg:text-5xl leading-[1.05]"
            data-testid="banner-headline"
          >
            {headline}
          </h2>
          <p className="mt-2 text-indigo-100/90 text-[15px] max-w-2xl" data-testid="banner-sub">
            {sub}
          </p>
        </div>

        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 shrink-0">
          <button
            type="button"
            onClick={toggleAutonomous}
            disabled={execSaving}
            data-testid="banner-autonomous-toggle"
            data-autonomous-mode={isAuto ? "on" : "off"}
            className={`group inline-flex items-center gap-2 rounded-full px-3.5 py-2 text-[12px] font-semibold transition-all border backdrop-blur-sm ${
              isAuto
                ? "bg-emerald-400/20 border-emerald-300/40 text-emerald-50 hover:bg-emerald-400/30"
                : "bg-white/10 border-white/20 text-white/90 hover:bg-white/20"
            } disabled:opacity-60`}
            title={
              isAuto
                ? "Autonomous Mode ON — AI is executing outreach automatically"
                : "Assisted Mode — each action needs your approval"
            }
          >
            <span
              className={`h-2 w-2 rounded-full ${
                isAuto ? "bg-emerald-300 animate-pulse" : "bg-white/60"
              }`}
            />
            <span>Autonomous {isAuto ? "ON" : "OFF"}</span>
          </button>
          <Link to={hasHotLeads ? "/leads?intent=high" : "/leads"} data-testid="banner-engage-cta-link">
            <Button
              data-testid="banner-engage-cta"
              className="h-12 px-5 rounded-xl bg-white text-[#002FA7] hover:bg-white/90 font-bold shadow-lg hover:scale-[1.03] transition-transform"
            >
              <Zap className="h-4 w-4 mr-2" />
              {hasHotLeads ? "Engage Now" : "Explore Leads"}
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
}

// Priority 2: TODAY'S PLAN CARD — three numbered items with counts.
function TodayPlanCard({ counts }) {
  const { high, warm, meetings } = counts;
  const items = [
    { k: "high", icon: Flame, color: "text-orange-600", bg: "bg-orange-50",
      label: "Engage high-intent leads", count: high },
    { k: "warm", icon: Workflow, color: "text-amber-600", bg: "bg-amber-50",
      label: "Follow up warm deals", count: warm },
    { k: "meetings", icon: Trophy, color: "text-emerald-600", bg: "bg-emerald-50",
      label: "Close booked meetings", count: meetings },
  ];
  return (
    <div
      data-testid="today-plan-card"
      className="h-full rounded-xl border border-slate-200 bg-white p-5 flex flex-col"
    >
      <div className="flex items-center gap-2">
        <div className="h-8 w-8 rounded-lg bg-indigo-50 grid place-items-center">
          <Brain className="h-4 w-4 text-indigo-600" />
        </div>
        <div>
          <h3 className="text-[13px] font-bold text-slate-900 tracking-tight">
            Today's Plan
          </h3>
          <p className="text-[11px] text-slate-500">AI-recommended priorities</p>
        </div>
      </div>
      <ol className="mt-4 space-y-2.5 flex-1" data-testid="today-plan-list">
        {items.map((it, idx) => {
          const Icon = it.icon;
          return (
            <li
              key={it.k}
              className={`flex items-center gap-3 rounded-lg border border-slate-100 px-3 py-2.5 ${
                it.count > 0 ? "bg-white" : "bg-slate-50/60 opacity-70"
              }`}
              data-testid={`today-plan-item-${it.k}`}
            >
              <div className={`h-8 w-8 rounded-md ${it.bg} grid place-items-center`}>
                <Icon className={`h-4 w-4 ${it.color}`} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[12px] text-slate-500">Step {idx + 1}</div>
                <div className="text-[13px] font-semibold text-slate-900 truncate">
                  {it.label}
                </div>
              </div>
              <div
                className="font-mono text-2xl font-extrabold text-slate-800 tabular-nums"
                data-testid={`today-plan-count-${it.k}`}
              >
                {it.count}
              </div>
            </li>
          );
        })}
      </ol>
      <Link to="/leads?intent=high" className="mt-4" data-testid="today-plan-execute-link">
        <Button
          disabled={high + warm + meetings === 0}
          data-testid="today-plan-execute-btn"
          className="w-full h-10 rounded-md bg-slate-900 hover:bg-slate-800 text-white"
        >
          <Rocket className="h-4 w-4 mr-2" />
          Execute Plan
        </Button>
      </Link>
    </div>
  );
}

// Priority 4: LIVE INTENT FEED — stock-ticker vibe; events animate in.
// Also hosts the opt-in "🔔 Get notified for hot leads" toggle (Task 2).
function LiveIntentFeed({ events }) {
  // Notifications opt-in — persisted in localStorage so user doesn't have to
  // re-enable on every visit. Two states: idle (toggle shown) and on (sent
  // permission request + currently armed).
  const [notifOn, setNotifOn] = useState(() => {
    try { return localStorage.getItem("profit10x_notif_on") === "1"; }
    catch { return false; }
  });
  const seenIdsRef = useRef(new Set());
  const lastFiredAtRef = useRef(0);

  // Seed seenIds with the first render — only events that arrive AFTER this
  // mount should ever trigger a notification (avoids blasting stale hot leads).
  useEffect(() => {
    events.forEach((ev) => seenIdsRef.current.add(ev.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fire notifications for NEW hot events (kind==="hot") while opt-in is ON.
  // Rate-limited: max 1 notification per 30 seconds.
  useEffect(() => {
    if (!notifOn) return;
    if (typeof window === "undefined" || !("Notification" in window)) return;
    if (Notification.permission !== "granted") return;

    const now = Date.now();
    for (const ev of events) {
      if (seenIdsRef.current.has(ev.id)) continue;
      seenIdsRef.current.add(ev.id);
      // Only fire on HOT events — matches the "hot lead notification" spec.
      if (ev.kind !== "hot") continue;
      if (now - lastFiredAtRef.current < 30_000) continue;

      try {
        const n = new Notification("🔥 New High-Intent Lead", {
          body: `${ev.lead.name || "Lead"} · ${ev.headline}`,
          tag: `profit10x-hot-${ev.lead.id}`,
          icon: "/favicon.ico",
          requireInteraction: false,
        });
        n.onclick = () => {
          window.focus();
          window.location.href = `/leads/${ev.lead.id}`;
        };
        lastFiredAtRef.current = now;
      } catch {
        // Safari/older browsers may throw — swallow silently.
      }
    }
  }, [events, notifOn]);

  const toggleNotifications = async () => {
    if (notifOn) {
      setNotifOn(false);
      try { localStorage.setItem("profit10x_notif_on", "0"); } catch { /* ignore */ }
      toast.success("Hot-lead notifications turned off");
      return;
    }
    if (typeof window === "undefined" || !("Notification" in window)) {
      toast.error("This browser doesn't support desktop notifications");
      return;
    }
    let perm = Notification.permission;
    if (perm !== "granted" && perm !== "denied") {
      try { perm = await Notification.requestPermission(); } catch { perm = "denied"; }
    }
    if (perm === "granted") {
      setNotifOn(true);
      try { localStorage.setItem("profit10x_notif_on", "1"); } catch { /* ignore */ }
      toast.success("You'll get a ping the moment a hot lead surfaces");
      // Demo "welcome" notification so the user sees it works immediately.
      try {
        const n = new Notification("🔔 Notifications armed", {
          body: "You'll see hot-lead pings here. Max 1 per 30s.",
          tag: "profit10x-notif-welcome",
          icon: "/favicon.ico",
        });
        n.onclick = () => window.focus();
      } catch { /* ignore */ }
    } else {
      toast.error("Notifications blocked", {
        description: "Allow notifications in your browser settings to enable this.",
      });
    }
  };

  return (
    <div
      data-testid="live-intent-feed"
      className="h-full rounded-xl border border-slate-200 bg-white p-5 flex flex-col"
    >
      <div className="flex items-center gap-2">
        <div className="h-8 w-8 rounded-lg bg-rose-50 grid place-items-center">
          <Radio className="h-4 w-4 text-rose-600 animate-pulse" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-[13px] font-bold text-slate-900 tracking-tight">
            Live Intent Feed
          </h3>
          <p className="text-[11px] text-slate-500">
            Real-time buying signals from your pipeline
          </p>
        </div>
        <button
          type="button"
          onClick={toggleNotifications}
          data-testid="feed-notif-toggle"
          data-notif-on={notifOn ? "on" : "off"}
          title={
            notifOn
              ? "Notifications ON — max 1 per 30s for hot leads"
              : "Get notified for hot leads"
          }
          className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold border transition-colors ${
            notifOn
              ? "bg-indigo-50 text-indigo-800 border-indigo-200 hover:bg-indigo-100"
              : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
          }`}
        >
          {notifOn ? (
            <BellRing className="h-3 w-3 animate-pulse" />
          ) : (
            <Bell className="h-3 w-3" />
          )}
          {notifOn ? "Notifying" : "Notify me"}
        </button>
        <span className="inline-flex items-center gap-1 text-[11px] text-emerald-700 font-semibold">
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
          LIVE
        </span>
      </div>
      {events.length === 0 ? (
        <div className="mt-5 flex-1 grid place-items-center text-center">
          <div className="text-[13px] text-slate-500 max-w-xs">
            No buying signals yet. Upload leads and let AI surface hot signals here.
          </div>
        </div>
      ) : (
        <ul
          className="mt-4 space-y-2 overflow-y-auto flex-1"
          data-testid="live-intent-feed-list"
        >
          {events.map((ev, i) => (
            <FeedRow key={ev.id} ev={ev} delay={i * 60} />
          ))}
        </ul>
      )}
    </div>
  );
}

function FeedRow({ ev, delay }) {
  const { lead, kind, headline } = ev;
  const icon =
    kind === "hot" ? Flame :
    kind === "meeting" ? Trophy :
    Sparkles;
  const Icon = icon;
  const tone =
    kind === "hot" ? "text-orange-600 bg-orange-50" :
    kind === "meeting" ? "text-emerald-600 bg-emerald-50" :
    "text-indigo-600 bg-indigo-50";
  return (
    <li
      data-testid={`feed-row-${lead.id}`}
      data-kind={kind}
      className="animate-[fadeSlide_400ms_ease-out_both]"
      style={{ animationDelay: `${delay}ms` }}
    >
      <Link
        to={`/leads/${lead.id}`}
        className="flex items-start gap-3 px-3 py-2 rounded-lg hover:bg-slate-50 transition-colors"
      >
        <div className={`h-7 w-7 rounded-md ${tone} grid place-items-center shrink-0 mt-0.5`}>
          <Icon className="h-3.5 w-3.5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="text-[13px] font-semibold text-slate-900 truncate">
              {lead.name || lead.contact_name || "Lead"}
            </span>
            <span className="text-[11px] text-slate-400">
              · {lead.company || lead.company_name || "—"}
            </span>
          </div>
          <div className="text-[12px] text-slate-600 truncate" title={headline}>
            {headline}
          </div>
        </div>
      </Link>
    </li>
  );
}

// Priority 6 + 9 + 13: HIGH-INTENT LEAD CARDS (Story Mode) with intensity
// badges, signals, "Why this?" reasoning, AI insight, Engage CTA.
function HighIntentStoryCards({ highIntentLeads }) {
  return (
    <section data-testid="high-intent-cards" className="space-y-3">
      <div className="flex items-end justify-between">
        <div>
          <h3 className="text-[13px] font-bold text-slate-900 tracking-tight flex items-center gap-2">
            <Flame className="h-4 w-4 text-orange-600" />
            High-Intent Leads
            <span className="inline-flex items-center gap-1 rounded-full bg-indigo-50 text-indigo-700 px-2 py-0.5 text-[10px] font-semibold border border-indigo-200">
              AI Suggested
            </span>
          </h3>
          <p className="text-[11px] text-slate-500">Best opportunities right now</p>
        </div>
        <Link
          to="/leads?intent=high"
          className="text-[12px] text-indigo-700 hover:text-indigo-900 font-semibold flex items-center gap-1"
          data-testid="high-intent-view-all-link"
        >
          See all <ArrowRight className="h-3 w-3" />
        </Link>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        {highIntentLeads.map(({ lead, hot, intentHigh, replied, score }) => (
          <HighIntentCard
            key={lead.id}
            lead={lead}
            hot={hot}
            intentHigh={intentHigh}
            replied={replied}
            score={score}
          />
        ))}
      </div>
    </section>
  );
}

function HighIntentCard({ lead, hot, intentHigh, replied, score }) {
  // Intensity — 🔥🔥🔥 based on composite score.
  let fires = 1;
  if (score >= 80) fires = 3;
  else if (score >= 65) fires = 2;

  // "Why this?" reasoning — derived from concrete fields (never fabricated).
  const reasons = [];
  if (hot) reasons.push("AI flagged as a hot lead this cycle");
  if (intentHigh) reasons.push("Intent label = High");
  if (replied) reasons.push("Replied with positive sentiment");
  if (lead.recent_signal) reasons.push(lead.recent_signal);
  if ((lead.icp_score ?? 0) >= 80) reasons.push(`ICP fit ${lead.icp_score}%`);
  const cleanReasons = reasons.slice(0, 3);

  const aiInsight =
    lead.priority_reason ||
    (replied ? "Lead is engaging — strike while interest is fresh." :
     intentHigh ? "Likely evaluating tools — move fast with a tailored angle." :
     "Strong fit — worth a personalized touch this week.");

  return (
    <div
      data-testid={`high-intent-card-${lead.id}`}
      data-intent-fires={fires}
      className="group rounded-xl border border-slate-200 bg-white p-4 hover:border-indigo-300 hover:shadow-md transition-all hover:-translate-y-0.5"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span
              className="inline-flex items-center gap-0.5 text-[10px] font-bold uppercase tracking-wider text-orange-700"
              title={`Intent intensity ${fires}/3`}
            >
              {"🔥".repeat(fires)} High Intent
            </span>
          </div>
          <div className="mt-1.5 text-[14px] font-bold text-slate-900 truncate">
            {lead.name || lead.contact_name || "Lead"}
          </div>
          <div className="text-[12px] text-slate-500 truncate">
            {lead.title || "—"} · {lead.company || lead.company_name || "—"}
          </div>
        </div>
        <div
          className="shrink-0 rounded-md bg-slate-50 border border-slate-200 px-2 py-1 font-mono text-[11px] font-semibold text-slate-700"
          title="AI confidence"
        >
          {Math.min(99, score)}%
        </div>
      </div>

      {cleanReasons.length > 0 && (
        <div className="mt-3">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">
            Why this lead?
          </div>
          <ul className="space-y-1">
            {cleanReasons.map((r, i) => (
              <li
                key={`${lead.id}-reason-${(r || "").slice(0, 40)}`}
                className="text-[12px] text-slate-700 flex items-start gap-1.5"
                data-testid={`high-intent-reason-${lead.id}-${i}`}
              >
                <span className="text-indigo-500 mt-0.5">→</span>
                <span className="min-w-0 flex-1">{r}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="mt-3 rounded-md bg-indigo-50/60 border border-indigo-100 px-2.5 py-2">
        <div className="flex items-center gap-1.5">
          <Brain className="h-3 w-3 text-indigo-600" />
          <div className="text-[10px] font-semibold uppercase tracking-wider text-indigo-700">
            AI Insight
          </div>
        </div>
        <div className="mt-0.5 text-[12px] text-slate-700 leading-snug">
          {aiInsight}
        </div>
      </div>

      <Link to={`/leads/${lead.id}`} className="mt-3 block" data-testid={`high-intent-engage-${lead.id}`}>
        <Button className="w-full h-9 rounded-md bg-[#002FA7] hover:bg-[#002072] text-white group-hover:scale-[1.02] transition-transform">
          <Zap className="h-3.5 w-3.5 mr-1.5" />
          Engage
        </Button>
      </Link>
    </div>
  );
}

// Priority 12: SYSTEM HEALTH BAR — four service chips from /api/system/health.
function SystemHealthBar({ health }) {
  const cb = health?.scalability?.circuit_breakers || {};
  const aq = health?.async_queue || {};
  const rows = [
    {
      key: "email",
      label: "Email",
      icon: Send,
      status: cb?.resend?.open ? "degraded" : "healthy",
      note: cb?.resend?.open ? "Resend breaker open" : "Delivery green",
    },
    {
      key: "calls",
      label: "Calls",
      icon: Phone,
      status: cb?.vapi?.open ? "degraded" : "healthy",
      note: cb?.vapi?.open ? "VAPI breaker open" : "Voice agent ready",
    },
    {
      key: "queue",
      label: "Queue",
      icon: Rocket,
      status: aq?.broker_reachable
        ? ((aq?.total_pending ?? 0) > 20 ? "degraded" : "healthy")
        : "down",
      note: aq?.broker_reachable
        ? `${aq?.total_pending ?? 0} pending`
        : "Broker unreachable",
    },
    {
      key: "crm",
      label: "CRM",
      icon: Workflow,
      status: cb?.crm?.open ? "degraded" : "healthy",
      note: cb?.crm?.open ? "CRM breaker open" : "Sync green",
    },
  ];
  return (
    <section
      className="rounded-xl border border-slate-200 bg-white px-4 py-3"
      data-testid="system-health-bar"
    >
      <div className="flex items-center gap-2 flex-wrap">
        <div className="inline-flex items-center gap-2 text-[12px] font-semibold text-slate-700 mr-2">
          <Shield className="h-3.5 w-3.5 text-slate-500" />
          System Health
        </div>
        {rows.map((r) => (
          <HealthChip key={r.key} row={r} />
        ))}
        <div className="flex-1" />
        <div className="text-[11px] text-slate-400">Refreshes every 30s</div>
      </div>
    </section>
  );
}

function HealthChip({ row }) {
  const tone =
    row.status === "healthy" ? "bg-emerald-50 text-emerald-800 border-emerald-200" :
    row.status === "degraded" ? "bg-amber-50 text-amber-800 border-amber-200" :
    "bg-red-50 text-red-700 border-red-200";
  const dot =
    row.status === "healthy" ? "bg-emerald-500" :
    row.status === "degraded" ? "bg-amber-500" :
    "bg-red-500";
  const Icon = row.icon;
  return (
    <span
      className={`inline-flex items-center gap-2 rounded-full px-2.5 py-1 text-[11px] font-medium border ${tone}`}
      data-testid={`health-chip-${row.key}`}
      data-status={row.status}
      title={row.note}
    >
      <span
        className={`h-1.5 w-1.5 rounded-full ${dot} ${
          row.status !== "down" ? "animate-pulse" : ""
        }`}
      />
      <Icon className="h-3 w-3" />
      {row.label}
    </span>
  );
}

// ============ MINI BURST PANEL (Task 3) ============
// Compact always-on view of the async queue execution — shows running,
// completed-today, and failed counts across recent automation_jobs.
// Hidden when there are no jobs to avoid cold-start noise. Polls /api/jobs
// every 5s via the parent Dashboard.
function MiniBurstPanel({ jobs }) {
  if (!jobs || jobs.length === 0) return null;
  // Anchor "today" at local midnight to keep the completed count stable-ish.
  const todayStart = (() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d.getTime();
  })();
  let completed = 0, running = 0, failed = 0, retrying = 0;
  for (const j of jobs) {
    const status = (j.status || "").toLowerCase();
    const attempts = Number(j.attempts || 0);
    const updatedMs = Date.parse(j.updated_at || j.created_at || 0) || 0;
    if (status === "running") running += 1;
    else if (status === "failed") failed += 1;
    else if (status === "completed" && updatedMs >= todayStart) completed += 1;
    if ((status === "queued" || status === "running") && attempts >= 1) retrying += 1;
  }
  const lastUpdate = jobs[0]?.updated_at || jobs[0]?.created_at;
  const pretty = lastUpdate ? timeAgo(lastUpdate) : "moments ago";

  return (
    <Link
      to="/leads"
      data-testid="mini-burst-panel"
      className="block rounded-xl border border-slate-200 bg-white px-4 py-3 hover:border-indigo-300 hover:shadow-md transition-all group"
    >
      <div className="flex items-center gap-3 flex-wrap">
        <div className="inline-flex items-center gap-2">
          <div className="h-8 w-8 rounded-lg bg-indigo-50 grid place-items-center">
            <Rocket className="h-4 w-4 text-indigo-600" />
          </div>
          <div>
            <div className="text-[13px] font-bold text-slate-900 tracking-tight flex items-center gap-1.5">
              AI Sales Engine
              {running > 0 && (
                <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-full px-1.5 py-0.5">
                  <Zap className="h-2.5 w-2.5 animate-pulse" />
                  Executing
                </span>
              )}
            </div>
            <div className="text-[11px] text-slate-500">
              AI is executing outreach in real time · updated {pretty}
            </div>
          </div>
        </div>
        <div className="flex-1" />
        <MiniStat
          testid="mini-burst-completed"
          icon={CheckCircle2}
          iconClass="text-emerald-600"
          count={completed}
          label="done today"
        />
        <MiniStat
          testid="mini-burst-running"
          icon={Workflow}
          iconClass="text-amber-600"
          count={running}
          label="running"
          pulse={running > 0}
        />
        <MiniStat
          testid="mini-burst-retry"
          icon={ArrowRight}
          iconClass="text-indigo-600"
          count={retrying}
          label="retrying"
        />
        <MiniStat
          testid="mini-burst-failed"
          icon={XCircle}
          iconClass="text-red-600"
          count={failed}
          label="failed"
        />
        <span className="text-[11px] text-indigo-700 font-semibold group-hover:underline inline-flex items-center gap-1 ml-2">
          Open Burst <ArrowRight className="h-3 w-3" />
        </span>
      </div>
    </Link>
  );
}

function MiniStat({ icon: Icon, iconClass, count, label, testid, pulse = false }) {
  return (
    <div
      data-testid={testid}
      data-count={count}
      className="inline-flex items-center gap-1.5 rounded-full bg-slate-50 border border-slate-100 px-2.5 py-1"
    >
      <Icon className={`h-3.5 w-3.5 ${iconClass} ${pulse ? "animate-pulse" : ""}`} />
      <span className="font-mono text-[13px] font-bold tabular-nums text-slate-900">
        {count}
      </span>
      <span className="text-[11px] text-slate-500">{label}</span>
    </div>
  );
}

function timeAgo(iso) {
  const t = Date.parse(iso);
  if (!t) return "moments ago";
  const s = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (s < 5) return "now";
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return "today";
}

// ============ MEETINGS BOOKED TODAY (Task: Live counter) ============
// Counts leads that hit MEETING_BOOKED lifecycle whose booking timestamp is
// from today (local midnight). Falls back to "all-time booked" when a lead
// has no explicit meeting timestamp — safer than hiding the metric on
// cold-seed data. Number ramps from previous value to new with rAF.
function MeetingsBookedTodayCard({ leads }) {
  const target = useMemo(() => {
    const startOfDay = new Date();
    startOfDay.setHours(0, 0, 0, 0);
    const startMs = startOfDay.getTime();
    let todayCount = 0;
    let allTime = 0;
    for (const l of leads || []) {
      if ((l.lifecycle || "").toUpperCase() !== "MEETING_BOOKED") continue;
      allTime += 1;
      const ts = Date.parse(
        l.meeting_booked_at || l.meeting_at || l.updated_at || l.sent_at || "",
      );
      if (ts && ts >= startMs) todayCount += 1;
    }
    // If no lead carries a timestamp (demo-seed), show all-time so the KPI
    // doesn't flatline at zero and undersell the pipeline.
    return todayCount > 0 ? todayCount : allTime;
  }, [leads]);

  const display = useAnimatedCount(target);

  return (
    <div
      data-testid="meetings-booked-today-card"
      data-count={target}
      className="relative overflow-hidden rounded-xl border border-emerald-200 bg-gradient-to-br from-emerald-50 via-white to-teal-50 p-5 transition-transform hover:-translate-y-0.5 hover:shadow-md"
    >
      <div
        aria-hidden
        className="absolute -top-10 -right-10 h-32 w-32 rounded-full bg-emerald-300/20 blur-2xl"
      />
      <div className="relative flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-emerald-800">
            <CalendarCheck className="h-3.5 w-3.5" />
            Meetings Booked Today
          </div>
          <div
            className="mt-2 font-display font-extrabold text-slate-900 tabular-nums text-5xl tracking-tight"
            data-testid="meetings-booked-today-count"
          >
            {display}
          </div>
          <div className="mt-1 inline-flex items-center gap-1 text-[11px] text-emerald-700 font-medium">
            <Sparkles className="h-3 w-3" />
            Generated by AI SDR
          </div>
        </div>
        <div className="shrink-0 h-10 w-10 rounded-lg bg-emerald-100 grid place-items-center">
          <TrendingUp className="h-5 w-5 text-emerald-600" />
        </div>
      </div>
    </div>
  );
}

// ============ SHARE DEMO CTA (Task: Viral distribution) ============
// Small single-purpose card — copies a preset-linked demo URL to clipboard.
// No backend work: the receiving session is the existing demo fallback +
// an `?intent=high` query param that our Leads page already respects.
function ShareDemoCard() {
  const [copied, setCopied] = useState(false);
  // Dynamic share URL — always uses the current origin so the link works in
  // every environment (preview pod, staging, production). Prevents the
  // "copied a prod URL from a preview" footgun during internal demos.
  const shareUrl =
    typeof window !== "undefined"
      ? `${window.location.origin}/?preset=hot-leads`
      : "/?preset=hot-leads";

  const onShare = async () => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(shareUrl);
      } else {
        // Fallback for non-secure contexts.
        const el = document.createElement("textarea");
        el.value = shareUrl;
        el.setAttribute("readonly", "");
        el.style.position = "absolute";
        el.style.left = "-9999px";
        document.body.appendChild(el);
        el.select();
        document.execCommand("copy");
        document.body.removeChild(el);
      }
      setCopied(true);
      toast.success("Demo link copied — share with your team");
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error("Couldn't copy", {
        description: `Here's the link: ${shareUrl}`,
      });
    }
  };

  return (
    <div
      data-testid="share-demo-card"
      className="relative overflow-hidden rounded-xl border border-indigo-200 bg-gradient-to-br from-indigo-50 via-white to-sky-50 p-5 flex flex-col justify-between gap-3"
    >
      <div>
        <div className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-indigo-800">
          <Share2 className="h-3.5 w-3.5" />
          Share this Demo
        </div>
        <div className="mt-2 text-[15px] font-bold text-slate-900 leading-tight">
          Let your team see it in action.
        </div>
        <div className="mt-1 text-[12px] text-slate-500">
          One-click copy. Recipient lands on a curated hot-leads view.
        </div>
      </div>
      <Button
        type="button"
        onClick={onShare}
        data-testid="share-demo-btn"
        data-copied={copied ? "yes" : "no"}
        className="h-10 rounded-md bg-[#002FA7] hover:bg-[#002072] text-white hover:scale-[1.02] transition-transform"
      >
        {copied ? (
          <>
            <CheckCircle2 className="h-4 w-4 mr-2" />
            Link copied
          </>
        ) : (
          <>
            <Share2 className="h-4 w-4 mr-2" />
            Share Demo
          </>
        )}
      </Button>
    </div>
  );
}

// ============ PIPELINE GENERATED (outcome KPI — existing data) ============
function PipelineGeneratedCard({ impact }) {
  // Try a few common shapes from the existing /api/ai-impact payload.
  const rawAmount =
    impact?.revenue_influenced ??
    impact?.pipeline_generated ??
    impact?.pipeline ??
    impact?.pipeline_influenced ??
    null;
  const target = typeof rawAmount === "number" && rawAmount > 0 ? rawAmount : 0;
  const display = useAnimatedCount(target);

  // Format ₹ value compactly: 12,34,567 / 12.3L / 1.2Cr
  const pretty = (n) => {
    if (!n || n <= 0) return "—";
    if (n >= 10_000_000) return `₹${(n / 10_000_000).toFixed(1)}Cr`;
    if (n >= 100_000) return `₹${(n / 100_000).toFixed(1)}L`;
    if (n >= 1_000) return `₹${(n / 1_000).toFixed(1)}k`;
    return `₹${n}`;
  };

  return (
    <div
      data-testid="pipeline-generated-card"
      data-count={target}
      className="relative overflow-hidden rounded-xl border border-amber-200 bg-gradient-to-br from-amber-50 via-white to-orange-50 p-5 transition-transform hover:-translate-y-0.5 hover:shadow-md"
    >
      <div
        aria-hidden
        className="absolute -bottom-10 -left-10 h-32 w-32 rounded-full bg-amber-300/20 blur-2xl"
      />
      <div className="relative flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-amber-800">
            <TrendingUp className="h-3.5 w-3.5" />
            Pipeline Generated
          </div>
          <div
            className="mt-2 font-display font-extrabold text-slate-900 tabular-nums text-4xl tracking-tight"
            data-testid="pipeline-generated-amount"
          >
            {pretty(display)}
          </div>
          <div className="mt-1 text-[11px] text-amber-800 font-medium">
            Last 7 days · AI-attributed
          </div>
        </div>
        <div className="shrink-0 h-10 w-10 rounded-lg bg-amber-100 grid place-items-center">
          <Sparkles className="h-5 w-5 text-amber-600" />
        </div>
      </div>
    </div>
  );
}

// Smoothly ramp a number from its previous render value to `target` using
// requestAnimationFrame. Cheap: runs at most a couple of hundred ms per
// counter change.
function useAnimatedCount(target, { durationMs = 700 } = {}) {
  const [display, setDisplay] = useState(target);
  const previousRef = useRef(target);
  useEffect(() => {
    const from = previousRef.current ?? 0;
    const to = Number(target) || 0;
    if (from === to) return;
    let raf = 0;
    const start = performance.now();
    const tick = (now) => {
      const t = Math.min(1, (now - start) / durationMs);
      // easeOutCubic — snappy start, soft landing.
      const eased = 1 - Math.pow(1 - t, 3);
      const val = Math.round(from + (to - from) * eased);
      setDisplay(val);
      if (t < 1) raf = requestAnimationFrame(tick);
      else previousRef.current = to;
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, durationMs]);
  return display;
}

